/** Lead detail drawer — Phase 2's "detail editing", deliberately small:
 * status, notes, ARV overrides, and tasks for this address. Notes save on
 * blur AND on a short debounce; every keystroke is already safe locally
 * because the outbox owns durability, not this component. ARV values write
 * v1's exact LEAD_EDITS contract (listedArv/compedArv as "$X,XXX" strings +
 * the `arv` mirror), so v1's detail and ARV fallbacks pick them up as-is. */
import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../shell/icons";
import type { UiPrefs } from "../settings/uiPrefs";
import { moneyExact } from "../../lib/money";
import { fmtMoney, type Lead } from "../../types";
import { natKey } from "../../lib/natkey";
import { liveOn } from "../../lib/liveFire";
import { newIdemKey } from "../../lib/idem";
import { backendFetch } from "../../lib/backend";
import {
  arvOverrideOf, fmtMoneyEdit, isHotReply, isTaskDone, normPhone, notesOf, parseMoneyEdit, statusOf, sellerOf,
  mediaLinksOf, leadLinkedContacts, leadActivity,
  LEAD_DETAIL_FIELDS, SELLER_ROLES, STATUS_OPTIONS, OPT_PTYPE, OPT_LABEL, OPT_YESNO,
  OPT_FLOOD, OPT_WATER, OPT_SEWER, OPT_ARCH, OPT_ROOF, OPT_CONSTR, OPT_FOUND_TYPE, OPT_PARKING,
  type EditsMap, type TaskRec, type SellerContact, type MediaLink,
} from "../edits/api";
import { fmtDateEdit, toDateInput } from "../deals/api";
import { recoverContactThread, contactLabel, isPersonName, fmtPhone } from "../inbox/api";
import { MoneyText } from "../shared/MoneyText";
import { PhotoLightbox } from "../shared/PhotoLightbox";
import { DealEngineCard } from "./DealEngineCard";
import { DuplicateBanner } from "./DuplicateBanner";
import { deKeywords } from "./dealEngine";
import { CompsPanel } from "../comps/CompsPanel";
import { ConfirmDelete, useConfirmDelete } from "../common/ConfirmDelete";
import { type CompRow, type CompsFilters, type CompsSubject, type SavedComp } from "../comps/api";
import { ArvSection } from "../arv/ArvSection";
import { arvCompute, type ArvResult } from "../arv/api";
import { EmailsSection, type MailLoaders } from "../mail/EmailsSection";
import { PermitsSection } from "../permits/PermitsSection";
import type { AiPermitResult } from "../permits/api";
import { ConvertSection } from "../convert/ConvertSection";
import { MatchedBuyersPanel } from "../buyers/MatchedBuyersPanel";
import { targetFromLead } from "../buyers/match";
import type { Buyer } from "../buyers/api";
import { RehabCalculator } from "../rehab/RehabCalculator";
import { narrowRehabCalc, rehabTotal, type RehabCalcState } from "../rehab/api";
import { leadRoi, nrOverrideFor } from "../roi/estMoney";
import { usd, flipResult, brrrResult, type NrInputs, type BrrrResult } from "../roi/math";
import { RoiCalculator } from "../roi/RoiCalculator";
import { EsignModal } from "../esign/EsignModal";
import { FarbarModal } from "../farbar/FarbarModal";
import { farbarSeedFor } from "../farbar/api";
import { PastContactPicker } from "../contacts/PastContactPicker";
import { PAST_KEYS, type PastContact, type PastContacts } from "../contacts/pastContacts";

/** v1 buildSharePDF — a printable investor one-pager (Save-as-PDF via the
 * browser's print dialog). Pure client-side; no backend. */
function shareDocHtml(
  address: string,
  mode: "flip" | "brrrr" | "both",
  g: NrInputs,
  fr: { profit: number; roi: number; total: number },
  br: BrrrResult,
): string {
  const m = (n: number): string => moneyExact(n);
  const pc = (n: number): string => (Number.isFinite(n) ? n.toFixed(1) + "%" : "∞");
  const esc = (s: string): string => s.replace(/[&<>]/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;"));
  const label = mode === "brrrr" ? "BRRRR" : mode === "both" ? "Fix &amp; Flip + BRRRR" : "Fix &amp; Flip";
  const flip = `<h2>Fix &amp; Flip</h2>
<div class="kpis">
<div class="kpi"><span>After Repair Value</span><strong>${m(g.arv)}</strong></div>
<div class="kpi"><span>Purchase Price</span><strong>${m(g.purchase)}</strong></div>
<div class="kpi"><span>Rehab Estimate</span><strong>${m(g.rehab)}</strong></div>
<div class="kpi"><span>Total Investment</span><strong>${m(fr.total)}</strong></div>
<div class="kpi"><span>Net Profit</span><strong class="good">${m(fr.profit)}</strong></div>
<div class="kpi"><span>ROI</span><strong class="good">${pc(fr.roi)}</strong></div>
</div>`;
  const brr = `<h2>BRRRR &middot; Buy, Rehab, Rent, Refinance, Repeat</h2>
<div class="kpis">
<div class="kpi"><span>After Repair Value</span><strong>${m(br.arv)}</strong></div>
<div class="kpi"><span>Total Cash In</span><strong>${m(br.totalCashIn)}</strong></div>
<div class="kpi"><span>Refinance Loan</span><strong>${m(br.refiLoan)}</strong></div>
<div class="kpi"><span>Cash Left In</span><strong>${m(br.cashLeft)}</strong></div>
<div class="kpi"><span>Monthly Rent</span><strong>${m(br.rent)}</strong></div>
<div class="kpi"><span>Monthly Cash Flow</span><strong class="${br.cfMo >= 0 ? "good" : "bad"}">${m(br.cfMo)}</strong></div>
<div class="kpi"><span>Cash-on-Cash</span><strong class="${Number.isNaN(br.coc) || br.coc < 0 ? "bad" : "good"}">${br.coc === Infinity ? "∞" : Number.isNaN(br.coc) ? "—" : pc(br.coc)}</strong></div>
<div class="kpi"><span>Cap Rate</span><strong>${pc(br.capRate)}</strong></div>
<div class="kpi"><span>Forced Equity</span><strong class="good">${m(br.forcedEquity)}</strong></div>
</div>`;
  const body = (mode === "flip" || mode === "both" ? flip : "") + (mode === "brrrr" || mode === "both" ? brr : "");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(address)} — Investor Summary</title>
<style>body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;color:#0f172a;max-width:720px;margin:32px auto;padding:0 20px}
h1{font-size:22px;margin:0}h2{font-size:15px;margin:26px 0 0;color:#0f172a}.tag{color:#2563eb;font-weight:700;letter-spacing:.05em;font-size:12px;text-transform:uppercase}
.sub{color:#64748b;margin:2px 0 20px}.kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:12px 0}
.kpi{border:1px solid #e2e8f0;border-radius:10px;padding:12px}.kpi span{display:block;color:#64748b;font-size:11px;text-transform:uppercase}
.kpi strong{font-size:18px}.good{color:#059669}.bad{color:#dc2626}
.foot{margin-top:24px;color:#94a3b8;font-size:11px}button{margin-top:20px;background:#2563eb;color:#fff;border:0;border-radius:8px;padding:9px 16px;font-weight:700;cursor:pointer}
@media print{button{display:none}}</style></head><body>
<div class="tag">ETO Property Group</div>
<h1>${esc(address)}</h1>
<div class="sub">Investor deal summary &middot; ${label}</div>
${body}
<button onclick="window.print()">Print / Save as PDF</button>
<div class="foot">Generated by REI CRM &middot; figures are estimates for discussion only.</div>
</body></html>`;
}

export function LeadDrawer({
  lead,
  onRenameLead,
  edits,
  tasks,
  outcomeLog,
  onClose,
  onStatus,
  onNotes,
  onArv,
  onField,
  onAddTask,
  onToggleTask,
  onPatchTask,
  onRemoveTask,
  onCompsPersist,
  onCompsArv,
  compsSearch,
  compsPhotos,
  onRunArv,
  onSaveArvResult,
  mailLoaders,
  onAiPermitSearch,
  dealKeys,
  wholesaleKeys,
  onConvert,
  buyers,
  onSaveRehab,
  onSaveSeller,
  onSaveMediaLinks,
  nrMaster,
  nrBy,
  onSaveNrOverride,
  uiPrefs = {},
  onSaveUiPrefs,
  hiddenFields,
  onDelete,
  members,
  isAdmin = false,
  token,
  initialTab = null,
  onTabChange,
  dupOthers,
  onOpenDup,
  onEnrich,
  threads,
  onSendSms,
  onCallLead,
  pastContacts,
}: {
  lead: Lead;
  edits: EditsMap;
  tasks: TaskRec[];
  outcomeLog: unknown;
  onClose: () => void;
  onStatus: (address: string, status: string) => void;
  onNotes: (address: string, notes: string) => void;
  onArv: (address: string, kind: "listedArv" | "compedArv", value: number | null) => void;
  onField: (address: string, key: string, value: string | null) => void;
  onAddTask: (title: string, opts?: { deal?: string; due?: string; who?: string; pri?: string }) => void;
  onToggleTask: (key: string, done: boolean) => void;
  onPatchTask?: (key: string, patch: { who?: string; due?: string; pri?: string }) => void;
  onRemoveTask?: (key: string) => void;
  onCompsPersist: (address: string, state: { filters: CompsFilters; saved: Record<string, SavedComp> }) => void;
  onCompsArv: (address: string, arv: number) => void;
  compsSearch: (address: string, f: CompsFilters, onWake: () => void) => Promise<{ comps: CompRow[]; subject: CompsSubject | null }>;
  compsPhotos: (comp: CompRow) => Promise<string[]>;
  onRunArv: (address: string, sqft: number, beds: number | null, onWake: () => void) => Promise<ArvResult>;
  onSaveArvResult: (address: string, result: ArvResult) => void;
  mailLoaders: MailLoaders;
  onAiPermitSearch: (address: string, county: string, leadId: string) => Promise<AiPermitResult>;
  dealKeys: Set<string>;
  wholesaleKeys: Set<string>;
  onConvert: (lead: Lead, kind: "flip" | "wholesale") => void;
  buyers: Buyer[];
  onSaveRehab: (address: string, state: RehabCalcState, total: number) => void;
  onSaveSeller: (address: string, seller: SellerContact | null) => void;
  /** rename the lead's ADDRESS (identity key) — rare, server re-keys everything */
  onRenameLead?: (oldAddress: string, newAddress: string) => Promise<void>;
  onSaveMediaLinks: (address: string, links: MediaLink[]) => void;
  nrMaster: NrInputs;
  /** org NR_BY blob — the per-property ROI overrides */
  nrBy?: unknown;
  /** persist this property's ROI override (v1 owned this; v2 does now) */
  onSaveNrOverride?: (address: string, vals: Record<string, number>) => void;
  /** per-user interface prefs — which tabs this person pinned */
  uiPrefs?: UiPrefs;
  /** omit to hide the pin control entirely (read-only contexts) */
  onSaveUiPrefs?: (next: UiPrefs) => void;
  hiddenFields: Set<string>;
  onDelete?: (lead: Lead) => void;
  members?: { id: string; name: string }[];
  /** only admins may (re)assign a lead; others see the owner read-only (v1 parity) */
  isAdmin?: boolean;
  /** Bearer token for backend calls (BoldSign e-sign). Optional so LeadsPage —
   * this file's only consumer — keeps compiling; forward `token={token}` there
   * to make Send-for-Signature live (LeadsPage already has `token` in scope). */
  token?: string | null;
  /** restore the drawer's inner tab after a refresh (v1 _TT0 parity) */
  initialTab?: string | null;
  onTabChange?: (tab: string) => void;
  /** v1 recheckMarket — pulls Zestimate/tax/history/county/flood/permits for
   * this lead; resolves with the summary line shown next to the button. */
  /** the other side(s) of a duplicate group + open-the-twin (2026-08-17) */
  dupOthers?: { address: string; source: string; status: string }[];
  onOpenDup?: (address: string) => void;
  onEnrich?: (lead: Lead, onStep: (msg: string) => void) => Promise<string>;
  /** comms threads (Inbox data) — the drawer shows THIS lead's SMS thread */
  threads?: import("../inbox/api").Thread[];
  /** go-live item 5 (quoSend): send an SMS to this lead — resolves on server confirm */
  onSendSms?: (to: string, text: string, leadKey: string, name?: string, idemKey?: string) => Promise<void>;
  /** go-live item 6 (quoCall): click-to-call this lead */
  onCallLead?: (to: string, leadKey: string, name?: string) => Promise<void>;
  /** past title companies / dispo contacts across all leads+deals (2026-08-31) */
  pastContacts?: PastContacts | undefined;
}) {
  const k = natKey(lead.address);
  const fields = edits.get(k);
  const status = statusOf(fields);
  const arvOverride = arvOverrideOf(fields);
  // Zillow rows carry the listing agent in the scraped blob (agent_name /
  // agent_phone) but nobody typed a Seller card, so the card sat EMPTY and
  // the realtor looked "not added" (Tom 2026-08-20, 1502 Egan Dr — Bethanne
  // Baer was in raw the whole time). Typed contact always wins; the agent
  // only fills silence. Saving any edit persists the whole card to fields.
  const sellerShown = useMemo(() => {
    const typed = sellerOf(fields);
    if (typed) return typed;
    const name = lead.facts?.["who"] ?? "";
    const phone = lead.facts?.["phone"] ?? "";
    if (!name && !phone) return null;
    const co = (lead.seller ?? "").split(" · ")[1] ?? "";
    return { name, role: "Listing Agent", phone, email: "", company: co };
  }, [fields, lead]);
  const effArv = arvOverride ?? lead.arv;
  const [notes, setNotes] = useState(notesOf(fields));
  const [lbIndex, setLbIndex] = useState<number | null>(null);
  const LEAD_TAB_IDS = ["details", "photos", "comps", "listing", "tax", "history", "roi", "activity", "team", "ai", "permits", "emails"];
  const [tab, setTabRaw] = useState(() => (initialTab && LEAD_TAB_IDS.includes(initialTab) ? initialTab : "details"));
  // report tab changes up so a refresh restores the same tab (v1 _TT0 parity)
  const setTab = (t: string) => { setTabRaw(t); onTabChange?.(t); };
  const [followMsg, setFollowMsg] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  /** mobile action sheet: null = closed, "follow" = the follow-up dates,
   *  "all" = everything that moved out of the pinned bar. */
  const [mActs, setMActs] = useState<null | "follow" | "all">(null);
  const [pinOpen, setPinOpen] = useState(false);
  /** true once the tab strip is scrolled to its end — the right-edge fade turns
   *  off there, because a fade over the last tab implies more that isn't. */
  const [tabsAtEnd, setTabsAtEnd] = useState(false);
  /** Phone layout. Live, not one-shot: rotating the device must move the cards
   *  back, or a section would be unreachable in landscape. */
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 720px)");
    const on = () => setIsPhone(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [assignOpen, setAssignOpen] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);
  const [farbarOpen, setFarbarOpen] = useState(false);
  const [editTaskKey, setEditTaskKey] = useState<string | null>(null);
  const [photoUrlOpen, setPhotoUrlOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  // Enrich (v1 recheckMarket): busy + live step text + final summary note
  const [enBusy, setEnBusy] = useState(false);
  const [enNote, setEnNote] = useState<string | null>(null);
  // address rename (Tom 2026-08-11: "if i want to edit the address i cant?")
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renBusy, setRenBusy] = useState(false);
  const [renErr, setRenErr] = useState<string | null>(null);
  const doRename = async () => {
    const next = (renaming ?? "").trim();
    if (!onRenameLead || renBusy || next.length < 5 || next === lead.address) {
      if (next === lead.address) { setRenaming(null); setRenErr(null); }
      return;
    }
    setRenBusy(true);
    setRenErr(null);
    try {
      await onRenameLead(lead.address, next);
    } catch (e) {
      setRenErr(((e as { detail?: string; message?: string })?.detail
        ?? (e as Error)?.message ?? "Rename failed").slice(0, 120));
      setRenBusy(false);
    }
  };
  const savedNotes = useRef(notesOf(fields));
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Media links split: uploaded photos are stored as data: URLs (rendered as
  // gallery thumbnails), external gallery/video links keep their http(s) URLs.
  const mediaLinks = mediaLinksOf(fields);
  const uploadedIdx = mediaLinks.reduce<number[]>((acc, m, i) => {
    if (typeof m.url === "string" && m.url.startsWith("data:")) acc.push(i);
    return acc;
  }, []);
  const uploadedPhotos = uploadedIdx.map((i) => mediaLinks[i]!.url);
  const extLinks = mediaLinks.filter((m) => typeof m.url === "string" && !m.url.startsWith("data:"));
  const allPhotos = [...lead.photos, ...uploadedPhotos];
  // v1 _allMediaLinks: scraped p.mediaLinks (read-only) + user-added external links.
  const scrapedMedia = lead.mediaLinks ?? [];
  const allExtLinks = [...scrapedMedia, ...extLinks];

  // Add a photo / video URL from the Photos tab (v1 addPhotoUrlPrompt) — saved
  // to the same mediaLinks door the LINKS card uses; video links auto-labelled.
  const addPhotoUrl = () => {
    const u = photoUrl.trim();
    if (!u) return;
    const label = /video|youtu|vimeo|tour|walk|\.mp4/i.test(u) ? "Video" : "Photos";
    onSaveMediaLinks(lead.address, [...mediaLinksOf(fields), { label, url: u }]);
    setPhotoUrl("");
    setPhotoUrlOpen(false);
  };

  // Upload Photos: compress each picked image to a small data URL and persist
  // it via the mediaLinks door so it shows in the gallery and sticks.
  const handleUpload = (files: FileList | null) => {
    const imgs = files ? Array.from(files).filter((f) => f.type.startsWith("image/")) : [];
    if (!imgs.length) return;
    void Promise.all(imgs.map(compressImage)).then((urls) => {
      const clean = urls.filter((u): u is string => typeof u === "string" && u.length > 0);
      if (clean.length) onSaveMediaLinks(lead.address, [...mediaLinksOf(fields), ...clean.map((u) => ({ label: "Uploaded Photo", url: u }))]);
    });
  };

  // KPI header (v1 detail KPI row) + county label + alreadyConverted gate.
  const purchaseVal = parseMoneyEdit(typeof fields?.["offerPrice"] === "string" ? (fields["offerPrice"] as string) : null) ?? lead.price ?? 0;
  // v1 p.rehab || rehabAuto: the itemized Rehab Calculator wins; else the
  // one-click Auto-numbers estimate (stored in edits.rehabAuto) fills the KPI/ROI.
  const rehabCalcVal = rehabTotal(narrowRehabCalc(fields?.["rehabCalc"]), lead);
  // this property's stored ROI override, via the same tolerant lookup the
  // rehab chain and investor matching use
  const nrSavedOver = useMemo(() => nrOverrideFor(nrBy, lead.address), [nrBy, lead.address]);
  // Exactly what the Net ROI tab is showing right now (incl. UNSAVED edits),
  // tagged with the address so a stale value from another lead can never leak
  // into this lead's printout. Feeds genDoc below.
  const nrLiveRef = useRef<{ addr: string; g: NrInputs } | null>(null);
  const rehabAutoVal = parseMoneyEdit(typeof fields?.["rehabAuto"] === "string" ? (fields["rehabAuto"] as string) : null) ?? 0;
  const rehabVal = rehabCalcVal > 0 ? rehabCalcVal : rehabAutoVal;
  const roiEst = leadRoi(purchaseVal, effArv, rehabVal, nrMaster);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoNote, setAutoNote] = useState<string | null>(null);
  // v1 deAutoNums: one click fills a rehab estimate (heuristic) + a comped ARV
  // (backend comps) when the lead has none yet — both through the edits door.
  const autoNumbers = async () => {
    if (autoBusy) return;
    setAutoBusy(true);
    setAutoNote(null);
    const est = autoRehabEstimate(lead, fields, notes);
    onField(lead.address, "rehabAuto", String(est));
    let arvMsg = "";
    const hasArv = arvOverride ?? lead.arv;
    if (!hasArv && lead.sqft) {
      try {
        const r = await onRunArv(lead.address, lead.sqft, lead.beds, () => setAutoNote("Backend waking up — retrying…"));
        onSaveArvResult(lead.address, r);
        const arv = arvCompute(r).arv;
        if (arv) { onArv(lead.address, "compedArv", Math.round(arv)); arvMsg = " · ARV $" + Math.round(arv).toLocaleString(); }
        else arvMsg = " · not enough comps for auto-ARV";
      } catch { arvMsg = " · ARV lookup failed"; }
    }
    setAutoNote("Auto rehab $" + est.toLocaleString() + arvMsg);
    setAutoBusy(false);
    setTimeout(() => setAutoNote(null), 5000);
  };
  const assigneeId = typeof fields?.["assignee"] === "string" ? (fields["assignee"] as string) : "";
  const assigneeName = members?.find((m) => m.id === assigneeId)?.name ?? "";
  const createFollowUp = (whenMs: number) => {
    const due = new Date(whenMs).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    onAddTask("Follow up: " + (lead.address.split(",")[0] || lead.address), { deal: lead.address, due, pri: "High" });
    setFollowOpen(false);
    setFollowMsg(true);
    setTimeout(() => setFollowMsg(false), 2500);
  };
  const genDoc = (mode: "flip" | "brrrr" | "both") => {
    // The investor summary must print the SAME numbers the Net ROI tab shows
    // (Tom 2026-08-19, Belgian Court: "Why is the info not the same?" — this
    // built from org defaults, ignoring the per-property save AND any unsaved
    // edits). Prefer the tab's live state; if the tab was never opened for
    // this lead, rebuild its exact seed: master + lead numbers + saved
    // override, in the calculator's own precedence order.
    const live = nrLiveRef.current;
    const g: NrInputs = live && live.addr === lead.address
      ? live.g
      : { ...nrMaster, arv: effArv ?? 0, purchase: purchaseVal, rehab: rehabVal,
          ...(nrSavedOver as Partial<NrInputs> | undefined) };
    const fr = flipResult(g, "hard");
    const br = brrrResult(g);
    setShareOpen(false);
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(shareDocHtml(lead.address, mode, g, fr, br));
    w.document.close();
  };
  const countyLabel = lead.county ? lead.county.replace(/\s*county\s*$/i, "") + " County" : "County unknown";
  const converted: null | "deal" | "wholesale" = dealKeys.has(k) ? "deal" : wholesaleKeys.has(k) ? "wholesale" : null;
  const TABS: [string, string][] = [
    ["details", "Details"], ["photos", "Photos"], ["comps", "Comps"], ["listing", "Listing"],
    ["tax", "Tax Info"], ["history", "History"], ["roi", "Net ROI"], ["activity", "Activity"],
    ["team", "Team"], ["ai", "AI Estimator"], ["permits", "Permits"], ["emails", "Emails"],
  ];
  /** On a phone the Details tab stacked FIFTEEN cards into one endless scroll —
   *  it is a two-column layout on desktop, and collapsing to one column just
   *  concatenates it. These seven are self-contained, so on mobile they become
   *  their own tabs and Details is left as the property facts.
   *
   *  Activity is MERGED rather than added: there was already an Activity tab
   *  (LeadActivitySection, a timeline) and ActivityLogCard is a different
   *  component. Two tabs called Activity would be worse than the scroll. */
  const PHONE_TABS: [string, string][] = [
    ["messages", "Messages"], ["links", "Links"], ["seller", "Seller"],
    ["notes", "Notes"], ["leadtasks", "Tasks"], ["rehabcalc", "Rehab calc"],
  ];
  /** Tom's order, 2026-07-30: "Details, Messages, Photos, Links, Listing, Tax,
   *  Rehab Calculator, Net Rio, Permits, Emails — the rest are what ever you
   *  want". Phone only — desktop has no Messages/Links/Rehab-calc tabs, so
   *  the sequence cannot exist there and its strip is left alone. */
  const PHONE_ORDER = ["details", "messages", "photos", "links", "listing", "tax", "rehabcalc", "roi", "permits", "emails"];
  const ALL_TABS = isPhone
    ? [...TABS, ...PHONE_TABS].sort((a, b) => {
        const ia = PHONE_ORDER.indexOf(a[0]), ib = PHONE_ORDER.indexOf(b[0]);
        // both listed: his order. One listed: it goes first. Neither: keep the
        // existing relative order (sort is stable) — "doesnt matter to me".
        return (ia === -1 ? PHONE_ORDER.length : ia) - (ib === -1 ? PHONE_ORDER.length : ib);
      })
    : TABS;
  const visibleTabs = ALL_TABS.filter(([id]) =>
    !(id === "roi" && hiddenFields.has("financials")) && !(id === "tax" && hiddenFields.has("taxLegal")));

  // PINNED TABS (2026-07-29, Tom). Twelve tabs wrapped onto three rows on a
  // phone — ~130px of chrome before any content, and on a wrapped grid it is
  // genuinely hard to see which one is active. Now one scrolling row.
  //
  // Pinning is MANUAL, and the order of everything else never changes. An
  // automatic most-used sort was the other option and is worse: the targets
  // move under you, so the tab you reach for by memory is somewhere else the
  // next time. Pinned tabs go first, the rest keep their declared order.
  const pinnedTabs = uiPrefs.leadTabs ?? [];
  const orderedTabs = useMemo(() => {
    const pinnedSet = new Set(pinnedTabs);
    const pinned = pinnedTabs
      .map((id) => visibleTabs.find(([t]) => t === id))
      .filter((t): t is [string, string] => !!t);
    return [...pinned, ...visibleTabs.filter(([id]) => !pinnedSet.has(id))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedTabs.join("|"), visibleTabs.map(([id]) => id).join("|")]);

  const togglePin = (id: string) => {
    const next = pinnedTabs.includes(id)
      ? pinnedTabs.filter((p) => p !== id)
      : [...pinnedTabs, id];
    onSaveUiPrefs?.({ ...uiPrefs, leadTabs: next });
  };

  // switching leads: reset the local notes buffer to the new lead's notes
  useEffect(() => {
    setNotes(notesOf(fields));
    savedNotes.current = notesOf(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k]);

  useEffect(() => {
    // when the photo lightbox or e-sign modal is up, Escape belongs to IT, not
    // the drawer. The Comps tab renders the tool inline and owns its own Esc
    // (its photo modal), so the drawer stays put while comps is showing.
    const esc = (e: KeyboardEvent) => e.key === "Escape" && tab !== "comps" && lbIndex === null && !esignOpen && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose, tab, lbIndex, esignOpen]);

  const commitNotes = (v: string) => {
    if (v === savedNotes.current) return;
    savedNotes.current = v;
    onNotes(lead.address, v);
  };

  const noteChange = (v: string) => {
    setNotes(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => commitNotes(v), 900);
  };

  // FLUSH on unmount, don't just cancel. Clearing the timer threw away
  // whatever was typed in the last 900ms, and onBlur — the only other commit
  // path — does not fire when the drawer closes on Escape. So the fast way to
  // close lost the tail of every note. Same pattern DispoFlyer already uses,
  // via a ref so the effect can stay dependency-free and still see the latest
  // value rather than the one captured on mount.
  const flushNotes = useRef<() => void>(() => {});
  flushNotes.current = () => { if (debounce.current) commitNotes(notes); };
  useEffect(() => () => {
    if (debounce.current) clearTimeout(debounce.current);
    flushNotes.current();
  }, []);

  const leadTasks = tasks.filter((t) => t.deal && natKey(t.deal) === k);
  const statuses = STATUS_OPTIONS.includes(status) ? STATUS_OPTIONS : [status, ...STATUS_OPTIONS];

  const arvRow = (
    <div className="arvrow">
      <div className="arvfield">
        <label className="lbl">Listed ARV</label>
        <MoneyText key={k + ":listed"} initial={typeof fields?.["listedArv"] === "string" ? (fields["listedArv"] as string) : null} placeholder="e.g. 300,000" onCommit={(v) => onArv(lead.address, "listedArv", parseMoneyEdit(v))} />
      </div>
      <div className="arvfield">
        <label className="lbl">Comped ARV</label>
        <MoneyText key={k + ":comped"} initial={typeof fields?.["compedArv"] === "string" ? (fields["compedArv"] as string) : null} placeholder="wins over listed" onCommit={(v) => onArv(lead.address, "compedArv", parseMoneyEdit(v))} />
      </div>
    </div>
  );

  return (
    <>
      <div className="pd">
        <button className="pdback" onClick={onClose}>← Back to Properties</button>
        <div className="pdhead">
          <div className="pdhwho">
            {lead.photos.length ? (
              <button className="pdthumb" onClick={() => setLbIndex(0)} title="Open gallery"><img src={lead.photos[0]} alt="" /></button>
            ) : <div className="pdthumb">🏠</div>}
            <div>
              {renaming !== null ? (
                <div className="pdrename">
                  <input
                    value={renaming}
                    autoFocus
                    disabled={renBusy}
                    onChange={(e) => setRenaming(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void doRename();
                      if (e.key === "Escape" && !renBusy) { setRenaming(null); setRenErr(null); }
                    }}
                  />
                  <button className="btn slim" disabled={renBusy || renaming.trim().length < 5} onClick={() => void doRename()}>{renBusy ? "…" : "Save"}</button>
                  <button className="btn slim ghost" disabled={renBusy} onClick={() => { setRenaming(null); setRenErr(null); }}>Cancel</button>
                  {renErr ? <span className="pdrenerr">{renErr}</span> : null}
                </div>
              ) : (
                <h1 className="pdtitle">
                  {lead.displayAddress || lead.address}
                  {onRenameLead ? (
                    <button className="pdrenbtn" title="Edit the address (renames the lead everywhere — tasks, texts, deals follow it)"
                      onClick={() => { setRenaming(lead.address); setRenErr(null); }}>✎</button>
                  ) : null}
                </h1>
              )}
              {onEnrich && liveOn("enrich") ? (
                <>
                  <button
                    className="pdenrich"
                    disabled={enBusy}
                    title="Pull Zestimate, tax, price history, county + permit links from Zillow/county"
                    onClick={() => {
                      if (enBusy) return;
                      setEnBusy(true);
                      setEnNote("Enriching from Zillow / county…");
                      onEnrich(lead, (msg) => setEnNote(msg))
                        .then((summary) => setEnNote(summary))
                        .catch(() => setEnNote("Enrich failed — check the backend"))
                        .finally(() => setEnBusy(false));
                    }}
                  >{enBusy ? "⏳ Enriching…" : "✨ Enrich"}</button>
                  {enNote ? <span className="pdenrichnote">{enNote}</span> : null}
                </>
              ) : (
                <button className="pdenrich" disabled title="Enrichment turns on at go-live">✨ Enrich</button>
              )}
              <div className="pdcounty"><span className="pin">◉</span> {countyLabel}</div>
              <div className="pdchips">
                <span className="pchip review pchipsel" title="Click to change lead status">
                  <select value={status} onChange={(e) => onStatus(lead.address, e.target.value)}>
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </span>
                {/* IL leads carry their marketplace page (Tom 2026-08-10:
                    "it should show the investorlift link") — the source chip
                    becomes the link. Other sources keep the plain chip. */}
                {lead.ilUrl && (!/zillow\.com/i.test(lead.ilUrl) || /zillow/i.test(lead.source)) ? (
                  <a className="pchip source pchiplink" href={lead.ilUrl} target="_blank" rel="noreferrer"
                     title="Open the InvestorLift listing">{lead.source} ↗</a>
                ) : (
                  <span className="pchip source">{lead.source}</span>
                )}
                <span className="pchip market">{lead.marketStatus === "on" ? "On Market" : "Off Market"}</span>
                {isHotReply(fields, Date.now()) ? <span className="pchip hot">🔥 HOT reply</span> : null}
                {lead.priceDrop ? <span className="pchip drop">Price ↓ {lead.priceDrop.pct}%</span> : null}
                {lead.reductions > 0 ? <span className="pchip drop">{lead.reductions} reduction{lead.reductions > 1 ? "s" : ""}</span> : null}
                {members && members.length && isAdmin ? (
                  <button className={"pchip assign" + (assigneeName ? " on" : "")} onClick={() => setAssignOpen(true)} title="Assign to a team member">
                    {assigneeName ? "👤 " + assigneeName : "○ Unassigned"}
                  </button>
                ) : assigneeName ? (
                  <span className="pchip assign on" title="Assigned team member">👤 {assigneeName}</span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="pdacts">
            <div className="pdfollowwrap">
              <button className="pdbtn" onClick={() => setFollowOpen((v) => !v)}>⏰ {followMsg ? "Follow-up added ✓" : "Follow up"}</button>
              {followOpen ? (
                <div className="pdfollowpop">
                  <div className="pdfollowhd">Follow up when?</div>
                  <button className="pdfollowopt" onClick={() => createFollowUp(Date.now() + 86400000)}>Tomorrow</button>
                  <button className="pdfollowopt" onClick={() => createFollowUp(Date.now() + 3 * 86400000)}>In 3 days</button>
                  <button className="pdfollowopt" onClick={() => createFollowUp(Date.now() + 7 * 86400000)}>In a week</button>
                  <label className="pdfollowdate">Pick a date
                    <input type="date" onChange={(e) => { const v = e.target.value; if (v) createFollowUp(new Date(v + "T12:00:00").getTime()); }} />
                  </label>
                </div>
              ) : null}
            </div>
            <div className="pdfollowwrap">
              <button className="pdbtn" onClick={() => setShareOpen((v) => !v)} title="Open a printable investor one-pager (Save as PDF)">📄 Share / Generate Doc</button>
              {shareOpen ? (
                <div className="pdfollowpop">
                  <div className="pdfollowhd">Generate one-pager</div>
                  <button className="pdfollowopt" onClick={() => genDoc("flip")}>Fix &amp; Flip</button>
                  <button className="pdfollowopt" onClick={() => genDoc("brrrr")}>BRRRR</button>
                  <button className="pdfollowopt" onClick={() => genDoc("both")}>Both (Flip + BRRRR)</button>
                </div>
              ) : null}
            </div>
            <button className="pdbtn" onClick={() => setFarbarOpen(true)} title="Generate a filled FARBAR AS-IS contract to send">📄 FARBAR Contract</button>
            <button className="pdbtn" onClick={() => setEsignOpen(true)} title="Prepare & send a PDF for e-signature (BoldSign)">📝 Send for Signature</button>
            {converted !== "deal" ? <button className="pdbtn blue" onClick={() => onConvert(lead, "flip")}>⇄ Make Transaction</button> : <span className="pchip market">✓ In Transactions</span>}
            {converted !== "wholesale" ? <button className="pdbtn violet" onClick={() => onConvert(lead, "wholesale")}>🤝 Make Wholesale</button> : <span className="pchip market">✓ In Wholesale</span>}
            {onDelete ? <button className="pdbtn danger" onClick={() => setConfirmDel(true)}>🗑 Delete</button> : null}
          </div>

          {/* The .pdmbar that used to sit here (2026-07-29 morning) is gone:
              the facts table below took over its three actions the same day,
              and the end-of-file CSS hid the bar everywhere — dead markup.
              Its ⋯ sheet SURVIVES, but rendered further down: it lived inside
              this .pdhead, and .pdhead is display:none on phones, so "More
              actions" flipped state and the sheet rendered into a hidden
              subtree. position:fixed does not rescue a child of display:none. */}
        </div>

        {/* Possible-duplicate banner: another live lead is the same house
            (partial-address twins that slipped past the unique-address index).
            Renders nothing when there are none. */}
        <DuplicateBanner address={lead.address} token={token ?? null} />

        {/* v1 typed-delete (market-backfill "Delete this lead permanently?") */}
        {confirmDel && onDelete ? (
          <ConfirmDelete
            message={"Delete this lead permanently?\n\n" + (lead.address || "")}
            onCancel={() => setConfirmDel(false)}
            onConfirm={() => { setConfirmDel(false); onDelete(lead); }}
          />
        ) : null}
        {assignOpen ? (
          <div className="cmpmodalscrim" onClick={() => setAssignOpen(false)}>
            <div className="cmpmodal" onClick={(e) => e.stopPropagation()}>
              <div className="cmpmodalbody">
                <div className="cmpmodalhead"><h3>Assign property</h3><button className="close" onClick={() => setAssignOpen(false)}>×</button></div>
                <div className="assignlist">
                  <button className={"assignrow" + (assigneeId === "" ? " on" : "")} onClick={() => { onField(lead.address, "assignee", null); setAssignOpen(false); }}>○ Unassigned</button>
                  {members?.map((m) => (
                    <button key={m.id} className={"assignrow" + (assigneeId === m.id ? " on" : "")} onClick={() => { onField(lead.address, "assignee", m.id); setAssignOpen(false); }}>👤 {m.name}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="pdkpis">
          <div className="pdkpi"><div className="pdkpil">Asking Price</div><div className="pdkpiv">{fmtMoney(lead.price)}</div></div>
          <div className="pdkpi"><div className="pdkpil">ARV</div><div className="pdkpiv">{effArv !== null ? fmtMoneyEdit(effArv) : "—"}</div></div>
          <div className="pdkpi"><div className="pdkpil">Rehab Est.</div><div className="pdkpiv">{rehabVal > 0 ? usd(rehabVal) : "—"}</div></div>
          <div className="pdkpi"><div className="pdkpil">Est. ROI</div><div className={"pdkpiv" + (roiEst.roi !== null && roiEst.roi >= 0 ? " good" : "")}>{roiEst.roi !== null ? roiEst.roi.toFixed(1) + "%" : "—"}</div></div>
          <div className="pdkpi"><div className="pdkpil">Beds / Baths</div><div className="pdkpiv">{lead.beds ?? "—"} / {lead.baths ?? "—"}</div></div>
          <div className="pdkpi"><div className="pdkpil">Sqft</div><div className="pdkpiv">{lead.sqft ? lead.sqft.toLocaleString() : "—"}</div></div>
        </div>

        {/* MOBILE FACTS TABLE (2026-07-29, Tom).
            Measured on his phone at 390x844: the header alone was 263px — 47%
            of all the chrome and nearly double the 155px KPI grid — because a
            90px thumbnail, the address, Enrich, the county and four status
            chips each took their own row. Five rows to state one address.

            Everything above the tabs is the same block of facts, so it is one
            table now: status, owner, source/market and county are label-value
            pairs exactly like Asking and ARV, and paired values share a row.
            Status and Owner keep the accent colour and a chevron so they still
            read as tappable — turning a chip into a row must not turn a control
            into text.

            The actions are rows too. As a pinned bar they were the only chunky
            filled thing left on the screen, they stacked a second bar above the
            bottom nav, and the sticky-bottom never actually pinned because the
            bar sits early in the DOM. As rows there is nothing to pin.

            The desktop header, KPI grid and action stack are hidden under
            720px; this replaces all three. Header 263px -> ~95px. */}
        <div className="pdmfacts">
          <div className="pdmfhead">
            {lead.photos.length ? (
              <button className="pdmfthumb" onClick={() => setLbIndex(0)} title="Open gallery"><img src={lead.photos[0]} alt="" /></button>
            ) : <div className="pdmfthumb">🏠</div>}
            <div className="pdmfaddr">{lead.address}</div>
            {onEnrich && liveOn("enrich") ? (
              <button
                className="pdmfenrich"
                disabled={enBusy}
                title="Pull Zestimate, tax, price history, county + permit links"
                onClick={() => {
                  if (enBusy) return;
                  setEnBusy(true);
                  setEnNote("Enriching from Zillow / county…");
                  onEnrich(lead, (msg) => setEnNote(msg))
                    .then((summary) => setEnNote(summary))
                    .catch(() => setEnNote("Enrich failed — check the backend"))
                    .finally(() => setEnBusy(false));
                }}
              >{enBusy ? "…" : "Enrich"}</button>
            ) : null}
          </div>
          {/* the enrich progress line only renders while it has something to
              say, so it costs nothing at rest */}
          {enNote ? <div className="pdmfnote">{enNote}</div> : null}
          <dl className="pdmftable">
            <div className="pdmfrow tap edit">
              <dt>Status</dt>
              <dd className="act">
                <select value={status} onChange={(e) => onStatus(lead.address, e.target.value)}>
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {/* same slug scheme as the Properties list's .lstatus pill, so
                    the value carries the status colour you already know from
                    out there — Needs Review is amber in both places, not
                    amber outside and generic blue in here */}
                <span className={"pdmfval s-" + status.toLowerCase().replace(/[^a-z]+/g, "-")}>{status}</span>
              </dd>
            </div>
            {members && members.length && isAdmin ? (
              <button className="pdmfrow tap asbtn edit" onClick={() => setAssignOpen(true)}>
                <dt>Owner</dt><dd className="act">{assigneeName || "Unassigned"} ›</dd>
              </button>
            ) : (
              <div className="pdmfrow"><dt>Owner</dt><dd>{assigneeName || "Unassigned"}</dd></div>
            )}
            <div className="pdmfrow"><dt>Source · market</dt><dd>
              {lead.ilUrl
                ? <a className="pdmflink" href={lead.ilUrl} target="_blank" rel="noreferrer">{lead.source} ↗</a>
                : lead.source} · {lead.marketStatus === "on" ? "on market" : "off market"}
            </dd></div>
            <div className="pdmfrow"><dt>County</dt><dd>{countyLabel}</dd></div>
            {isHotReply(fields, Date.now()) || lead.priceDrop || lead.reductions > 0 ? (
              <div className="pdmfrow"><dt>Flags</dt><dd className="flags">
                {isHotReply(fields, Date.now()) ? <span className="pchip hot">🔥 HOT reply</span> : null}
                {lead.priceDrop ? <span className="pchip drop">Price ↓ {lead.priceDrop.pct}%</span> : null}
                {lead.reductions > 0 ? <span className="pchip drop">{lead.reductions} reduction{lead.reductions > 1 ? "s" : ""}</span> : null}
              </dd></div>
            ) : null}
            {/* paired two-up: five number rows become three, and you read
                asking against ARV anyway */}
            <div className="pdmfrow num"><dt>Asking · ARV</dt><dd>{fmtMoney(lead.price)} · {effArv !== null ? fmtMoneyEdit(effArv) : "—"}</dd></div>
            <div className="pdmfrow num"><dt>Est. ROI · rehab</dt><dd className={roiEst.roi !== null && roiEst.roi >= 0 ? "good" : ""}>{roiEst.roi !== null ? roiEst.roi.toFixed(1) + "%" : "—"}<span className="pdmfmuted"> · {rehabVal > 0 ? usd(rehabVal) : "—"}</span></dd></div>
            <div className="pdmfrow num"><dt>Beds / baths · sqft</dt><dd>{lead.beds ?? "—"} / {lead.baths ?? "—"} · {lead.sqft ? lead.sqft.toLocaleString() : "—"}</dd></div>
          </dl>
          <div className="pdmfacts-acts card">
            {converted !== "deal" ? (
              <button className="pdmfrow tap asbtn primary" onClick={() => onConvert(lead, "flip")}>
                <dt>Make transaction</dt><dd className="act">›</dd>
              </button>
            ) : <div className="pdmfrow"><dt>Transaction</dt><dd>✓ added</dd></div>}
            <button className="pdmfrow tap asbtn primary" onClick={() => setMActs("follow")}>
              <dt>{followMsg ? "Follow-up added ✓" : "Follow up"}</dt><dd className="act">›</dd>
            </button>
            <button className="pdmfrow tap asbtn" onClick={() => setMActs("all")}>
              <dt>More actions</dt><dd className="act">›</dd>
            </button>
          </div>
        </div>

        <div className="pdtabswrap">
          {/* .pdtabs.atend has existed since the fade shipped and nothing ever
              set it — so the fade stayed on at the end of the scroll, implying
              a tab that wasn't there. */}
          <div
            className={"pdtabs" + (tabsAtEnd ? " atend" : "")}
            onScroll={(e) => {
              const el = e.currentTarget;
              const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
              setTabsAtEnd((p) => (p === end ? p : end));
            }}
          >
            {orderedTabs.map(([id, label]) => (
              <button key={id} className={"pdtab" + (tab === id ? " on" : "") + (pinnedTabs.includes(id) ? " pinned" : "")} onClick={() => setTab(id)}>
                {label}{id === "photos" && lead.photos.length ? <span className="tct">{lead.photos.length}</span> : null}
              </button>
            ))}
          </div>
          <button
            className="pdtabmore"
            aria-label="More tabs"
            onClick={(e) => {
              const strip = e.currentTarget.parentElement?.querySelector(".pdtabs");
              strip?.scrollBy({ left: Math.round(strip.clientWidth * 0.7), behavior: "smooth" });
            }}
          >›</button>
          {onSaveUiPrefs ? (
            <button className="pdtabpin" aria-label="Choose pinned tabs" title="Pin the tabs you use most" onClick={() => setPinOpen(true)}>
              <Icon name="pin" size={16} />
            </button>
          ) : null}
        </div>
        {/* ⋯ / follow-up sheet. OUTSIDE .pdhead on purpose: it used to render
            in there, .pdhead is display:none on phones, and a sheet inside a
            hidden ancestor opens nowhere — "Clicking more actions doesnt do
            anything?" (Tom, 2026-07-29). Same spot as the pin sheet, which
            never had the bug. */}
        {mActs ? (
          <div className="pdmscrim" onClick={() => setMActs(null)}>
            <div className="pdmsheet" onClick={(e) => e.stopPropagation()}>
              {mActs === "follow" ? (
                <>
                  <div className="pdmhd">Follow up when?</div>
                  <button className="pdmitem" onClick={() => { createFollowUp(Date.now() + 86400000); setMActs(null); }}>Tomorrow</button>
                  <button className="pdmitem" onClick={() => { createFollowUp(Date.now() + 3 * 86400000); setMActs(null); }}>In 3 days</button>
                  <button className="pdmitem" onClick={() => { createFollowUp(Date.now() + 7 * 86400000); setMActs(null); }}>In a week</button>
                  <label className="pdmdate">Pick a date
                    <input type="date" onChange={(e) => { const v = e.target.value; if (v) { createFollowUp(new Date(v + "T12:00:00").getTime()); setMActs(null); } }} />
                  </label>
                </>
              ) : (
                <>
                  <div className="pdmhd">Actions</div>
                  <button className="pdmitem" onClick={() => { setMActs(null); genDoc("flip"); }}>Share / generate doc — fix &amp; flip</button>
                  <button className="pdmitem" onClick={() => { setMActs(null); genDoc("brrrr"); }}>Share / generate doc — BRRRR</button>
                  <button className="pdmitem" onClick={() => { setMActs(null); setEsignOpen(true); }}>Send for signature</button>
                  {converted !== "wholesale" ? (
                    <button className="pdmitem" onClick={() => { setMActs(null); onConvert(lead, "wholesale"); }}>Make wholesale</button>
                  ) : null}
                  {onDelete ? (
                    <button className="pdmitem danger" onClick={() => { setMActs(null); setConfirmDel(true); }}>Delete lead</button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
        {pinOpen ? (
          <div className="pdmscrim" onClick={() => setPinOpen(false)}>
            <div className="pdmsheet" onClick={(e) => e.stopPropagation()}>
              <div className="pdmhd">Pin the tabs you use most</div>
              <p className="pdpinnote">Pinned tabs move to the front. Everything else keeps its order, so nothing shifts around on you.</p>
              {visibleTabs.map(([id, label]) => (
                <button key={id} className={"pdmitem pin" + (pinnedTabs.includes(id) ? " on" : "")} onClick={() => togglePin(id)}>
                  <Icon name="pin" size={15} />
                  <span>{label}</span>
                  {pinnedTabs.includes(id) ? <span className="pdpinon">Pinned</span> : null}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {tab === "details" ? (
          <div className="pddetails">
            <DupMergeBanner lead={lead} others={dupOthers ?? []} token={token} onOpenDup={onOpenDup} />
            <OverviewSection lead={lead} />
            <div className="pdbody">
              <div className="pdmain">
                <PropertyInfoCard lead={lead} fields={fields} onField={onField} />
                <AutoCompsCard lead={lead} onOpenComps={() => setTab("comps")} />
                {hiddenFields.has("financials") ? null : (
                  <FinancialsCard lead={lead} fields={fields} onField={onField} onArv={onArv} />
                )}
                <FieldCard title="PROPERTY CONDITION" group="condition" lead={lead} fields={fields} onField={onField} />
                <FieldCard title="DISPOSITION CONTACT" group="disposition" lead={lead} fields={fields} onField={onField} past={pastContacts} />
                <FieldCard title="TITLE COMPANY" group="title" lead={lead} fields={fields} onField={onField} past={pastContacts} />
              </div>
              <div className="pdside">
                {isPhone ? null : <LeadCommsCard
                  leadKey={k}
                  seller={sellerShown}
                  threads={threads ?? []}
                  onEmail={() => setTab("emails")}
                  {...(onSendSms ? { onSendSms } : {})}
                  {...(onCallLead ? { onCallLead } : {})}
                  onSaveSeller={(s) => onSaveSeller(lead.address, s)}
                  dispName={typeof fields?.["dispName"] === "string" ? (fields["dispName"] as string) : ""}
                  dispPhone={typeof fields?.["dispPhone"] === "string" ? (fields["dispPhone"] as string) : ""}
                  onSavePhone={(tgt, ph) => tgt === "dispo"
                    ? onField(lead.address, "dispPhone", ph)
                    : onSaveSeller(lead.address, { ...(sellerShown ?? { name: "", role: "", email: "", company: "", phone: "" }), phone: ph })}
                />}
                <PhotoThumbCard lead={lead} extra={uploadedPhotos} onViewAll={() => setTab("photos")} />
                {isPhone ? null : <LinksCard address={lead.address} fields={fields} onField={onField} links={mediaLinksOf(fields)} onSaveMediaLinks={onSaveMediaLinks} scraped={scrapedMedia} auto={{ zillowUrl: lead.zillowUrl ?? null }} />}
                {isPhone || hiddenFields.has("sellerContact") ? null : (
                  <SellerCard key={k + ":seller"} seller={sellerShown} onSave={(s) => onSaveSeller(lead.address, s)} />
                )}
                {isPhone ? null : <NotesCard value={notes} onChange={noteChange} onCommit={commitNotes} />}
                {isPhone ? null : <ActivityLogCard lead={lead} fields={fields} />}
              </div>
            </div>
            <div className="pdwide">
              {isPhone ? null : <RehabCalculator lead={lead} fields={fields} onSave={(state, total) => onSaveRehab(lead.address, state, total)} startOpen />}
              {hiddenFields.has("financials") ? null : (
                <QuickRoiCard lead={lead} fields={fields} onArv={onArv} onField={onField} rehabEst={rehabVal} />
              )}
              <div className="pdcard">
                <div className="pdsec">Tasks for this property</div>
                <div className="drawertasks">
                  {leadTasks.length === 0 ? <span className="sub">None yet.</span> : null}
                  {leadTasks.map((t) => {
                    const editing = editTaskKey === t.natKey;
                    const pri = (t.pri ?? "").toLowerCase();
                    return (
                      <div key={t.natKey} className={"taskrow slim" + (editing ? " editing" : "")}>
                        <input type="checkbox" checked={isTaskDone(t.status)} onChange={(e) => onToggleTask(t.natKey, e.target.checked)} />
                        <span className={"pridot " + (pri || "none")} title={t.pri ? t.pri + " priority" : "No priority"} />
                        <button className={"tasktitle asbtn" + (isTaskDone(t.status) ? " donetext" : "")} onClick={() => setEditTaskKey(editing ? null : t.natKey)}>
                          {t.title}
                          {t.due ? <span className="sub"> · due {t.due}</span> : null}
                          {t.who ? <span className="sub"> · {t.who}</span> : null}
                        </button>
                        {onRemoveTask ? <button className="taskdel" title="Delete task" onClick={() => { onRemoveTask(t.natKey); if (editing) setEditTaskKey(null); }}>×</button> : null}
                        {editing && onPatchTask ? (
                          <div className="taskedit" onClick={(e) => e.stopPropagation()}>
                            <div className="arvfield"><label className="lbl">Assign to</label><input className="moneyinput" defaultValue={t.who ?? ""} placeholder="e.g. Tom" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (t.who ?? "")) onPatchTask(t.natKey, { who: v }); }} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} /></div>
                            <div className="arvfield"><label className="lbl">Due date</label><input className="moneyinput" type="date" defaultValue={toDateInput(t.due)} onChange={(e) => onPatchTask(t.natKey, { due: e.target.value ? fmtDateEdit(e.target.value) : "" })} /></div>
                            <div className="arvfield"><label className="lbl">Priority</label><select className="moneyinput" value={t.pri ?? ""} onChange={(e) => onPatchTask(t.natKey, { pri: e.target.value })}><option value="">—</option><option>High</option><option>Medium</option><option>Low</option></select></div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {/* The free-text add box is gone (2026-07-29, Tom: "we already
                      have a follow up button which is tasks so whats the point").
                      Both wrote to the same store — onAddTask({deal}) — but this
                      one attached NO due date, and a task with no due date never
                      reaches the Overdue count or the reminder scheduler. So it
                      quietly created tasks that would not chase you. Follow up is
                      the one way in from here, and it always sets a date. Custom
                      titles still live on the Tasks page, which has the full
                      create form. */}
                  <div className="addtaskhint sub">
                    Use <b>Follow up</b> above to add a dated task, or the Tasks page for a custom one.
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="pdmain">

        {/* PHONE-ONLY TABS. Each renders the very same component the Details
            tab does on desktop — no duplicate implementations to drift. */}
        {isPhone && tab === "messages" ? (
          <div className="pdmain">
            <LeadCommsCard
              leadKey={k}
              seller={sellerShown}
              threads={threads ?? []}
              onEmail={() => setTab("emails")}
              {...(onSendSms ? { onSendSms } : {})}
              {...(onCallLead ? { onCallLead } : {})}
              onSaveSeller={(s) => onSaveSeller(lead.address, s)}
              dispName={typeof fields?.["dispName"] === "string" ? (fields["dispName"] as string) : ""}
              dispPhone={typeof fields?.["dispPhone"] === "string" ? (fields["dispPhone"] as string) : ""}
              onSavePhone={(tgt, ph) => tgt === "dispo"
                ? onField(lead.address, "dispPhone", ph)
                : onSaveSeller(lead.address, { ...(sellerShown ?? { name: "", role: "", email: "", company: "", phone: "" }), phone: ph })}
            />
          </div>
        ) : null}
        {isPhone && tab === "links" ? (
          <div className="pdmain">
            <LinksCard address={lead.address} fields={fields} onField={onField} links={mediaLinksOf(fields)} onSaveMediaLinks={onSaveMediaLinks} scraped={scrapedMedia} auto={{ zillowUrl: lead.zillowUrl ?? null }} />
          </div>
        ) : null}
        {isPhone && tab === "seller" ? (
          <div className="pdmain">
            {hiddenFields.has("sellerContact")
              ? <div className="cmempty">Seller contact is hidden for your role.</div>
              : <SellerCard key={k + ":seller"} seller={sellerShown} onSave={(s) => onSaveSeller(lead.address, s)} />}
          </div>
        ) : null}
        {isPhone && tab === "notes" ? (
          <div className="pdmain">
            <NotesCard value={notes} onChange={noteChange} onCommit={commitNotes} />
          </div>
        ) : null}
        {isPhone && tab === "leadtasks" ? (
          <div className="pdmain">
            <div className="pdcard">
              <div className="pdsec">Tasks for this property</div>
              <div className="drawertasks">
                {leadTasks.length === 0 ? <span className="sub">None yet.</span> : null}
                {leadTasks.map((t) => (
                  <label key={t.natKey} className={"drawertask" + (isTaskDone(t.status) ? " done" : "")}>
                    <input type="checkbox" checked={isTaskDone(t.status)} onChange={(e) => onToggleTask(t.natKey, e.target.checked)} />
                    <span className="dtitle">{t.title}</span>
                    {t.due ? <span className="sub">{t.due}</span> : null}
                  </label>
                ))}
                <div className="addtaskhint sub">
                  Use <b>Follow up</b> on the Details tab to add a dated task.
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {isPhone && tab === "rehabcalc" ? (
          <div className="pdmain">
            <RehabCalculator lead={lead} fields={fields} onSave={(state, total) => onSaveRehab(lead.address, state, total)} startOpen />
          </div>
        ) : null}

        {tab === "photos" && (
          <div className="photospanel">
            <div className="photoshd">
              <div>
                <div className="pdsec pdsecflush">Photos</div>
                <div className="sub">{allPhotos.length ? `${allPhotos.length} photo${allPhotos.length > 1 ? "s" : ""}${lead.photos.length ? " · " + lead.photos.length + " from listing" : ""}` : "No photos yet"}</div>
              </div>
              <div className="photosacts">
                <button className="btn slim" onClick={() => setPhotoUrlOpen((v) => !v)}>🔗 Add URL</button>
                <button className="btn slim primary" onClick={() => fileInputRef.current?.click()}>⬆ Upload Photos</button>
                <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => { handleUpload(e.target.files); e.currentTarget.value = ""; }} />
              </div>
            </div>
            {photoUrlOpen ? (
              <div className="photourladd">
                <input autoFocus placeholder="Paste a photo or video link (https://…)" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addPhotoUrl(); if (e.key === "Escape") setPhotoUrlOpen(false); }} />
                <button className="btn slim primary" onClick={addPhotoUrl}>Add</button>
              </div>
            ) : null}
            {allPhotos.length ? (
              <div className="photogallery">
                {allPhotos.map((u, i) => {
                  const up = i >= lead.photos.length;
                  const linkI = up ? uploadedIdx[i - lead.photos.length]! : -1;
                  return (
                    <div key={i} className="photothumbwrap">
                      <button className="photothumb" onClick={() => setLbIndex(i)} title="Open photo"><img src={u} alt="" loading="lazy" /></button>
                      {up ? <button className="photodel" title="Remove uploaded photo" onClick={() => onSaveMediaLinks(lead.address, mediaLinks.filter((_, j) => j !== linkI))}>×</button> : null}
                    </div>
                  );
                })}
              </div>
            ) : <div className="cmempty">No photos yet. Use “Add URL” or “Upload Photos” to attach some.</div>}
            {allExtLinks.length ? (
              <div className="photolinks">
                {allExtLinks.map((m, i) => {
                  const vid = /video|youtu|vimeo|tour|walk|\.mp4/i.test((m.label || "") + " " + m.url);
                  return <a key={i} href={m.url} target="_blank" rel="noreferrer" className={"photolink" + (vid ? " vid" : "")}>{vid ? "▶ " : "🖼 "}{m.label || "Photos"}</a>;
                })}
              </div>
            ) : null}
          </div>
        )}
        {tab === "comps" && (
          <CompsPanel
            lead={lead}
            fields={fields}
            inline
            onClose={() => {}}
            onSearch={compsSearch}
            onPhotos={compsPhotos}
            onPersist={onCompsPersist}
            onApplyArv={onCompsArv}
          />
        )}
        {tab === "listing" && (
          <>
            {hiddenFields.has("financials") ? null : (
              <DealEngineCard lead={lead} fields={fields} notes={notes} now={Date.now()} outcomeLog={outcomeLog} onField={onField} onAutoNumbers={() => void autoNumbers()} autoBusy={autoBusy} autoNote={autoNote} />
            )}
            <ListingSections lead={lead} fields={fields} onField={onField} hiddenFields={hiddenFields} rehabVal={rehabVal} roi={roiEst} countyLabel={countyLabel} />
            <ContactDocsCard docs={lead.photoLinks ?? []} />
            <DupMergeBanner lead={lead} others={dupOthers ?? []} token={token} onOpenDup={onOpenDup} />
            <OverviewSection lead={lead} />
            <PriceHistorySection lead={lead} />
          </>
        )}
        {tab === "tax" && (hiddenFields.has("taxLegal") ? (
          <div className="cmempty">Tax &amp; legal details are hidden for your role.</div>
        ) : (
          <>
            <TaxSections lead={lead} fields={fields} onField={onField} />
            <TaxHistorySection lead={lead} alwaysShow />
            <div className="sub taxdisclaimer">Values come from the county appraiser + Zillow and may lag the latest tax roll. Verify with the county before making an offer.</div>
          </>
        ))}
        {tab === "history" && (
          lead.priceDrops.length + lead.priceHistory.length === 0
            ? <div className="cmempty">No price history captured yet — it's pulled from the listing on the next scrape.</div>
            : <PriceHistorySection lead={lead} startOpen />
        )}
        {tab === "roi" && (
          hiddenFields.has("financials") ? (
            <div className="cmempty">Financial details are hidden for your role.</div>
          ) : (
            <RoiCalculator
              key={lead.id}
              master={nrMaster}
              seed={{ arv: effArv ?? 0, purchase: purchaseVal, rehab: rehabVal }}
              {...(onSaveNrOverride ? { address: lead.address, onSaveOver: onSaveNrOverride } : {})}
              {...(nrSavedOver ? { savedOver: nrSavedOver } : {})}
              onLive={(g) => { nrLiveRef.current = { addr: lead.address, g }; }}
            />
          )
        )}
        {tab === "activity" && <LeadActivitySection rows={leadActivity({ scraped: !lead.id.startsWith("m:"), source: lead.source, photos: lead.photos.length, owner: typeof fields?.["owner"] === "string" ? (fields["owner"] as string) : "" })} />}
        {tab === "team" && <LeadTeamSection contacts={leadLinkedContacts(fields)} />}
        {tab === "ai" && <ArvSection lead={lead} fields={fields} onRun={onRunArv} onSaveResult={onSaveArvResult} onField={onField} startOpen />}
        {tab === "permits" && <PermitsSection lead={lead} fields={fields} onAiSearch={onAiPermitSearch} startOpen />}
        {tab === "emails" && <EmailsSection address={lead.address} fields={fields} loaders={mailLoaders} startOpen />}
          </div>
        )}
      </div>
      {lbIndex !== null ? (
        <PhotoLightbox photos={allPhotos} index={lbIndex} onClose={() => setLbIndex(null)} />
      ) : null}
      {esignOpen ? (
        <EsignModal token={token ?? null} address={lead.address} onClose={() => setEsignOpen(false)} />
      ) : null}
      {farbarOpen ? (
        <FarbarModal
          token={token ?? null}
          isAdmin={isAdmin}
          seed={farbarSeedFor(lead, fields)}
          onClose={() => setFarbarOpen(false)}
        />
      ) : null}
    </>
  );
}

/** Seller contact card (v1 leads-list.js SELLER CONTACT). Read view with tap-to-
 * call/email; edit form writes the whole edits.seller object (feeds the deal
 * drawer's Contacts). "Remove" clears it (v1 sellerCleared). */
function SellerCard({ seller, onSave }: { seller: SellerContact | null; onSave: (s: SellerContact | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState<SellerContact>(seller ?? { name: "", role: "Wholesaler", phone: "", email: "", company: "" });
  const confirmDel = useConfirmDelete(); // v1 clearSeller — typed-delete gated
  const set = (k: keyof SellerContact, v: string) => setF((p) => ({ ...p, [k]: v }));
  const save = () => {
    if (!f.name.trim() && !f.phone.trim() && !f.email.trim() && !f.company.trim()) return;
    onSave(f);
    setEditing(false);
  };
  const startEdit = () => { setF(seller ?? { name: "", role: "Wholesaler", phone: "", email: "", company: "" }); setEditing(true); };

  return (
    <div className="detailsec">
      <div className="sechead static">
        <span>Seller contact</span>
        <span className="selleracts">
          <button className="rowbtn" title={seller ? "Edit" : "Add"} onClick={startEdit}>✎</button>
          {seller ? <button className="rowbtn del" title="Remove" onClick={() => confirmDel.ask("Remove seller contact?", () => onSave(null))}>✕</button> : null}
        </span>
      </div>
      {confirmDel.modal}
      {editing ? (
        <div className="secbody sellerform">
          <div className="arvfield"><label className="lbl">Name</label>
            <input className="moneyinput" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div className="sellerrow">
            <div className="arvfield"><label className="lbl">Role</label>
              <select className="moneyinput" value={f.role} onChange={(e) => set("role", e.target.value)}>
                {SELLER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select></div>
            <div className="arvfield"><label className="lbl">Phone</label>
              <input className="moneyinput" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          <div className="arvfield"><label className="lbl">Email</label>
            <input className="moneyinput" value={f.email} placeholder="seller@example.com" onChange={(e) => set("email", e.target.value)} /></div>
          <div className="arvfield"><label className="lbl">Company</label>
            <input className="moneyinput" value={f.company} onChange={(e) => set("company", e.target.value)} /></div>
          <div className="expformacts">
            <button className="btn slim primary" onClick={save}>Save</button>
            <button className="btn slim" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : seller ? (
        <div className="sellercard">
          <div className="sellertop"><strong>{seller.name || "—"}</strong><span className="mbpill hue-slate">{seller.role}</span></div>
          <div className="sellerlines">
            {seller.phone ? <a href={`tel:${seller.phone}`}>📞 {seller.phone}</a> : null}
            {seller.email ? <a href={`mailto:${seller.email}`}>✉️ {seller.email}</a> : null}
            {seller.company ? <span>🏢 {seller.company}</span> : null}
          </div>
        </div>
      ) : (
        <button className="dlink selleradd" onClick={startEdit}>+ Add seller contact</button>
      )}
    </div>
  );
}


function TextField({ initial, onCommit }: { initial: string | null; onCommit: (v: string | null) => void }) {
  const [text, setText] = useState(initial ?? "");
  const last = useRef(initial ?? "");
  const commit = () => {
    const v = text.trim();
    if (v === last.current) return;
    last.current = v;
    onCommit(v === "" ? null : v);
  };
  return (
    <input
      className="moneyinput"
      value={text}
      placeholder="—"
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
    />
  );
}


/** Reductions + listing history, read-only — the visible half of the
 * 2026-07-16 price rule: pre-CRM cuts are RECORDED here ("before import"),
 * in-CRM cuts carry the red tone; below it, Zillow's own listing events. */
/** v1 LISTING OVERVIEW (detail-arv.js _descHTML): the scraped description —
 * "all the info from whats under the big OVERVIEW" (Tom, 2026-07-14).
 * Collapsed to the first lines when long, exactly like v1's <details>. */
/** Duplicate banner + one-click merge (Tom 2026-08-17: "it shows duplicate
 * but it doesnt let me merge it" / "whats the other duplicate"). Names every
 * other member of the duplicate group, opens the twin, and merges them INTO
 * the lead being viewed — the twins' data fills this lead's empty slots and
 * the twins are retired (human decision: the sync can never revive them). */
function DupMergeBanner({ lead, others, token, onOpenDup }: {
  lead: Lead;
  others: { address: string; source: string; status: string }[];
  token: string | null | undefined;
  onOpenDup?: ((address: string) => void) | undefined;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const twins = others.length
    ? others
    : lead.possibleDupOf
      ? [{ address: lead.possibleDupOf, source: "other source", status: "" }]
      : [];
  if (!twins.length) return null;
  const merge = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      // Send the twins we're SHOWING — fingerprint pairs (masked house
      // numbers, e.g. 2361 vs 2362 Emerald Lake Dr) have different canon
      // addresses, so the server can't rediscover them on its own
      // (Tom 2026-08-19: "it tell me there is nothing to merge").
      const r = await backendFetch<{ kept: string; merged: { address: string }[]; reason?: string }>(
        token ?? null, "/api/inbox/merge-duplicate",
        { method: "POST", body: { keep_address: lead.address, absorb_addresses: twins.map((t) => t.address) } });
      const n = (r.merged ?? []).length;
      setDone(n
        ? `Merged — kept this lead, retired ${n} cop${n === 1 ? "y" : "ies"}. The list updates on the next sync.`
        : `Nothing merged — ${r.reason ?? "no other copy found in the database"}.`);
    } catch (e) {
      setDone("Merge failed — " + (e instanceof Error ? e.message : "try again shortly"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pdcard" style={{ border: "1px solid #f59e0b" }}>
      <div className="pdsec" style={{ color: "#f59e0b" }}>DUPLICATE — THE OTHER SIDE</div>
      <div className="overviewbody">
        {twins.map((t) => (
          <div key={t.address} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <span><b>{t.address}</b> ({t.source}{t.status ? " · " + t.status : ""})</span>
            {onOpenDup ? <button className="dlink" onClick={() => onOpenDup(t.address)}>Open it</button> : null}
          </div>
        ))}
        {done ? (
          <div style={{ marginTop: 4 }}>{done}</div>
        ) : (
          <button className="dlink" disabled={busy} onClick={() => void merge()} style={{ fontWeight: 600 }}>
            {busy ? "Merging…" : "⧉ Merge into this lead — keeps this one, retires the other"}
          </button>
        )}
      </div>
    </div>
  );
}

export function OverviewSection({ lead }: { lead: Lead }) {
  const [full, setFull] = useState(false);
  if (!lead.desc) return null;
  const long = lead.desc.length > 700;
  const shown = full || !long ? lead.desc : lead.desc.slice(0, 700);
  return (
    <>
    <div className="pdcard">
      <div className="pdsec">LISTING OVERVIEW</div>
      <div className="overviewbody">
        {shown}
        {long && !full ? "…" : ""}
        {long ? (
          <button className="dlink" onClick={() => setFull(!full)}>
            {full ? "Show less" : "Show full overview"}
          </button>
        ) : null}
      </div>
    </div>
    </>
  );
}

/** v1 PUBLIC TAX HISTORY (detail-arv.js _taxHistoryHTML): Year / Property
 * Tax / Assessment from the county via Zillow. */
export function TaxHistorySection({ lead, alwaysShow = false }: { lead: Lead; alwaysShow?: boolean }) {
  if (lead.taxHistory.length === 0) {
    if (!alwaysShow) return null;
    return (
      <div className="pdcard">
        <div className="pdsec">PUBLIC TAX HISTORY</div>
        <div className="sub">No public tax history captured yet — pulled from the county via Zillow on the next enrich.</div>
      </div>
    );
  }
  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>Public tax history</span>
        <span className="sub">{lead.taxHistory.length} year{lead.taxHistory.length > 1 ? "s" : ""}</span>
      </div>
        <div className="taxhist">
          <div className="taxrow taxhead">
            <span>Year</span>
            <span>Property Tax</span>
            <span>Assessment</span>
          </div>
          {lead.taxHistory.map((r, i) => (
            <div key={i} className="taxrow">
              <span>{r.year ?? "—"}</span>
              <span>{r.tax !== null ? fmtMoney(r.tax) : "—"}</span>
              <span>{r.assessment !== null ? fmtMoney(r.assessment) : "—"}</span>
            </div>
          ))}
        </div>
    </div>
  );
}

export function PriceHistorySection({ lead, startOpen = false }: { lead: Lead; startOpen?: boolean }) {
  const n = lead.priceDrops.length + lead.priceHistory.length;
  if (n === 0) return null;
  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>Price history</span>
        <span className="sub">
          {lead.priceDrops.length ? `${lead.priceDrops.length} reduction${lead.priceDrops.length > 1 ? "s" : ""} · ` : ""}
        </span>
      </div>
        <div className="pricehist">
          {lead.priceDrops.map((d, i) => (
            <div key={"d" + i} className={"phrow" + (d.preCrm ? "" : " phlive")}>
              <span className="phdate">{d.date ?? "—"}</span>
              <span className="phbody">
                Price reduced {d.from !== null ? fmtMoney(d.from) : "—"} → <strong>{d.to !== null ? fmtMoney(d.to) : "—"}</strong>
                {d.pct !== null ? ` (−${d.pct}%)` : ""}
              </span>
              {d.preCrm ? <span className="lbadge boff" title="Happened before this lead entered the CRM — recorded, never alerted">before import</span> : <span className="lbadge bdrop">in CRM</span>}
            </div>
          ))}
          {lead.priceHistory.map((h, i) => (
            <div key={"h" + i} className="phrow phdim">
              <span className="phdate">{h.date ?? "—"}</span>
              <span className="phbody">
                {h.event ?? "—"}
                {h.price !== null ? ` · ${fmtMoney(h.price)}` : ""}
                {h.pct !== null ? <em className={h.pct < 0 ? "phdown" : "phup"}> {h.pct > 0 ? "+" : ""}{h.pct}%</em> : null}
                {h.ppsf !== null ? ` · $${Math.round(h.ppsf)}/sqft` : ""}
              </span>
            </div>
          ))}
        </div>
    </div>
  );
}

/** v1 detail-arv.js teamPanel — Linked Contacts (derived from the lead's edits:
 * listing agent + owner). The assign/chat/shared-notes shells v1 shows are inert
 * there too (they persist nothing), so we render only the real, derived rows. */
function LeadTeamSection({ contacts }: { contacts: { name: string; role: string; phone: string }[] }) {
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Team · Linked contacts <span className="dim">({contacts.length})</span></span></div>
      {contacts.length === 0 ? <div className="sub">No linked contacts yet.</div> : (
        <div className="linkedcontacts">
          {contacts.map((c, i) => (
            <div key={i} className="linkedcontact">
              <span className="lcava">{c.name.split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase()}</span>
              <div className="lcmain">
                <div className="lctop"><strong>{c.name}</strong> <span className="mbpill hue-amber">{c.role}</span></div>
                {c.phone ? <a href={"tel:" + c.phone} className="lcphone">{c.phone}</a> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** v1 leads-list.js activityPanel — a derived summary (import source, photos
 * pulled, county match), not a timestamped audit. Empty for manual leads. */
function LeadActivitySection({ rows }: { rows: string[] }) {
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Activity</span></div>
      {rows.length === 0 ? <div className="sub">No activity yet.</div> : (
        <div className="leadactivity">
          {rows.map((r, i) => <div key={i} className="leadactrow"><span className="leadactdot" />{r}</div>)}
        </div>
      )}
    </div>
  );
}

/** v1 lead KPI "Est. ROI" tile — projected flip profit + ROI% (through the same
 * flipResult the Net ROI calculator runs). Shows "—" until price + ARV exist. */
function EstRoiTile({ roi }: { roi: import("../roi/estMoney").LeadRoi }) {
  const has = roi.roi !== null && roi.profit !== null;
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Est. ROI</span></div>
      {has ? (
        <div className="estroigrid">
          <div className="estroicell"><span className="estroil">Net profit</span><strong className={roi.profit! >= 0 ? "roigood" : "roibad"}>{usd(roi.profit!)}</strong></div>
          <div className="estroicell"><span className="estroil">Projected ROI</span><strong className={"roi-" + (roi.tier ?? "bad")}>{roi.roi!.toFixed(1)}%</strong></div>
          <div className="estroicell"><span className="estroil">ARV</span><strong>{roi.arv !== null ? usd(roi.arv) : "—"}</strong></div>
          <div className="estroicell"><span className="estroil">Rehab</span><strong>{usd(roi.rehab)}</strong></div>
        </div>
      ) : (
        <div className="sub">Add an offer/asking price and an ARV to see projected profit + ROI.</div>
      )}
    </div>
  );
}

/** v1 leads-list.js propLinkVal/editPropLink — the Zillow + Listing deep links
 * (edits.zillowUrl / edits.listingUrl), editable strings via the lead_fields door. */
function PropertyLinksSection({ address, fields, onField }: { address: string; fields: Record<string, unknown> | undefined; onField: (address: string, key: string, value: string | null) => void }) {
  const rows: [string, string][] = [["zillowUrl", "Zillow link"], ["listingUrl", "Listing link"]];
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const val = (k: string) => (typeof fields?.[k] === "string" ? (fields[k] as string) : "");
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Property links</span></div>
      {rows.map(([key, label]) => {
        const cur = val(key);
        return (
          <div key={key} className="proplinkrow">
            <span className="proplinklbl">{label}</span>
            {editing === key ? (
              <>
                <input
                  className="proplinkinput"
                  autoFocus
                  placeholder="https://…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { onField(address, key, draft.trim() || null); setEditing(null); } if (e.key === "Escape") setEditing(null); }}
                />
                <button className="btn slim" onClick={() => { onField(address, key, draft.trim() || null); setEditing(null); }}>Save</button>
              </>
            ) : cur ? (
              <>
                <a href={cur} target="_blank" rel="noreferrer" className="proplinkurl">{cur}</a>
                <button className="rowbtn" title="Edit" onClick={() => { setDraft(cur); setEditing(key); }}>✎</button>
              </>
            ) : (
              <button className="linkbtn" onClick={() => { setDraft(""); setEditing(key); }}>+ Add link</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---- v1 detail-panel cards (leads-list.js detailsPanel) ---------------------
// The Details tab is v1's two-column card layout: LEFT = titled field cards,
// RIGHT = comms/photos/links/seller/notes/activity, and a full-width Rehab +
// ROI block below. These helpers render each card; editing still writes the
// same LEAD_EDITS keys through onField / onArv as before.

const toNum = (s: string): number => {
  const n = Number(s.replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/** v1 deal-engine.js deAutoRehab — a heuristic rehab $ from the listing text,
 * condition fields, and year built. $/sqft tier (light→gut) + line-item adds
 * (roof/HVAC/plumbing/structure/termites/pool), rounded to the nearest $500. */
function autoRehabEstimate(lead: Lead, fields: Record<string, unknown> | undefined, notes: string): number {
  const f = (k: string): string => { const v = fields?.[k]; return typeof v === "string" ? v : ""; };
  const sqft = lead.sqft || 1200;
  const text = ((lead.desc ?? "") + " " + notes).toLowerCase();
  const kw = deKeywords(lead.desc ?? "", notes);
  const yr = toNum(f("yearBuilt")) || (lead.yearBuilt ?? 0);
  let rate = 25;
  if (/gut|fire damage|mold|condemned|complete rehab|full rehab|down to (the )?studs|uninhabitable/.test(text)) rate = 75;
  else if (kw.tier && (kw.tier.id === "investor" || kw.tier.id === "condition")) rate = 55;
  else if (/outdated|dated|original kitchen|original bath|needs updating|cosmetic|1970s|1980s interior/.test(text) || /dated|fair|poor/i.test(f("condition"))) rate = 40;
  if (yr && yr < 1980) rate += 5;
  const bad = (v: string) => /poor|bad|old|original|end of life|needs replac|leak|damag/i.test(v);
  let add = 0;
  if (bad(f("roofCond")) || toNum(f("roofAge")) > 15) add += 15000;
  if (bad(f("hvacCond")) || toNum(f("hvacAge")) > 12) add += 8000;
  if (bad(f("whCond"))) add += 2000;
  if (/galvanized|polybutylene|poly b|cast iron/i.test(f("plumb"))) add += 12000;
  if (f("structural") === "Yes" || f("foundN").trim()) add += 15000;
  if (f("termites").toLowerCase().startsWith("y")) add += 5000;
  if (/\bpool\b/.test(text) || /^y/i.test(f("pool"))) add += 8000;
  return Math.max(15000, Math.round((sqft * rate + add) / 500) * 500);
}

/** Downscale + JPEG-compress a picked image to a data URL so uploaded property
 * photos stay small enough to persist in the lead's saved links (no photo
 * storage backend yet). Longest side capped at 1400px, quality 0.72. */
function compressImage(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1400;
        let w = img.width, h = img.height;
        if (w > max || h > max) {
          if (w >= h) { h = Math.round((h * max) / w); w = max; }
          else { w = Math.round((w * max) / h); h = max; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try { resolve(canvas.toDataURL("image/jpeg", 0.72)); } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = typeof reader.result === "string" ? reader.result : "";
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

type FieldGroup = "financials" | "property" | "condition" | "disposition" | "title";

/** v1 kvGrid body for one LEAD_DETAIL_FIELDS group — always-open, editable. */
/** Moved to lib/usePhone (SOW needed it too — a shared hook must not force
 *  feature A to import feature B). Re-exported so existing imports hold. */
import { usePhone, focusOnMount } from "../../lib/usePhone";
export { usePhone, focusOnMount };

export function FieldGrid({ lead, fields, group, onField }: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  group: FieldGroup;
  onField: (address: string, key: string, value: string | null) => void;
}) {
  const isPhone = usePhone();
  /* Tom 2026-07-30, on the 24 property inputs: "i have to scroll down a lot to
     see all of them". Same decision as the tabs and the stage gate — EMPTY
     CHROME EARNS NO SPACE. On phones, filled fields are slim read rows (tap
     one to edit it) and the empties fold behind one "+ Fill a field" row.
     Desktop keeps the full input grid. */
  const [showEmpty, setShowEmpty] = useState(false);
  /** keys the user opened for editing this visit — they stay inputs even if
   *  cleared, so a field cannot vanish from under the finger mid-edit */
  const [editing, setEditing] = useState<Set<string>>(() => new Set());
  useEffect(() => { setShowEmpty(false); setEditing(new Set()); }, [lead.id]);
  const val = (key: string): string | null => {
    const v = fields?.[key];
    if (typeof v === "string" && v.trim()) return v;
    // Fall back to the raw twin enrich/scrape stored (lead.facts) — the data
    // was on the lead all along; only the edit-field was empty or the fields
    // feed hadn't landed (Tom 2026-08-16, "doesnt show info after the enrich").
    const f = lead.facts?.[key];
    return typeof f === "string" && f.trim() ? f : null;
  };
  const commitField = (key: string, v: string | null) =>
    onField(lead.address, key, v !== null && /phone/i.test(key) ? normPhone(v) || v : v);
  const groupFields = LEAD_DETAIL_FIELDS.filter((f) => f.group === group);
  // The money/text inputs are uncontrolled, seeded from `initial`. Keying on
  // the VALUE too means an external fill — the "pick from past" select, an
  // enrich — remounts the input with the new text instead of leaving it
  // showing stale emptiness (Tom 2026-08-31: "picking a title or contact
  // doesnt add the info in" — it saved fine, the box just never repainted).
  const fkey = (f: (typeof LEAD_DETAIL_FIELDS)[number]) => lead.id + f.key + ":" + (val(f.key) ?? "");
  const inputFor = (f: (typeof LEAD_DETAIL_FIELDS)[number]) =>
    f.kind === "money" ? (
      <MoneyText key={fkey(f)} initial={val(f.key)} placeholder="—" onCommit={(v) => commitField(f.key, v)} />
    ) : f.kind === "select" ? (
      <select
        key={lead.id + f.key}
        className="moneyinput"
        value={val(f.key) ?? ""}
        onChange={(e) => commitField(f.key, e.target.value || null)}
      >
        <option value="">—</option>
        {(f.opts ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        {val(f.key) && !(f.opts ?? []).includes(val(f.key) as string) ? (
          <option value={val(f.key) as string}>{val(f.key)}</option>
        ) : null}
      </select>
    ) : (
      <TextField key={fkey(f)} initial={val(f.key)} onCommit={(v) => commitField(f.key, v)} />
    );
  if (!isPhone) {
    return (
      <>
        {groupFields.map((f) => (
          <div key={f.key} className="kvcell">
            <div className="fld-l">{f.label}</div>
            {inputFor(f)}
          </div>
        ))}
      </>
    );
  }
  const isShown = (f: (typeof LEAD_DETAIL_FIELDS)[number]) => val(f.key) !== null || editing.has(f.key);
  const filled = groupFields.filter(isShown);
  const empty = groupFields.filter((f) => !isShown(f));
  return (
    <>
      {filled.map((f) =>
        editing.has(f.key) ? (
          <div key={f.key} className="kvcell" ref={focusOnMount}>
            <div className="fld-l">{f.label}</div>
            {inputFor(f)}
          </div>
        ) : (
          <button
            key={f.key}
            type="button"
            className="kvread"
            title="Tap to edit"
            onClick={() => setEditing((s) => new Set(s).add(f.key))}
          >
            <span className="kvread-l">{f.label}</span>
            <span className="kvread-v">{val(f.key)}</span>
          </button>
        ),
      )}
      {empty.length && !showEmpty ? (
        <button type="button" className="kvmore" onClick={() => setShowEmpty(true)}>
          + Fill a field ({empty.length} empty)
        </button>
      ) : null}
      {showEmpty
        ? empty.map((f) => (
            <div key={f.key} className="kvcell">
              <div className="fld-l">{f.label}</div>
              {inputFor(f)}
            </div>
          ))
        : null}
    </>
  );
}

/** Generic titled field card (PROPERTY CONDITION / DISPOSITION / TITLE). */
function FieldCard({ title, group, lead, fields, onField, past }: {
  title: string;
  group: FieldGroup;
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onField: (address: string, key: string, value: string | null) => void;
  /** past title companies / dispo contacts for the one-pick fill (2026-08-31) */
  past?: PastContacts | undefined;
}) {
  // Title + Disposition groups get a "pick from past" select that fills the
  // whole group in one tap (Tom 2026-08-31).
  const pickKind: "title" | "dispo" | null =
    group === "title" ? "title" : group === "disposition" ? "dispo" : null;
  const opts = pickKind && past ? past[pickKind] : [];
  const fillFrom = (c: PastContact) => {
    const keys = PAST_KEYS.lead[pickKind === "title" ? "title" : "dispo"] as Record<string, string>;
    const vals: Record<string, string> = { company: c.company, name: c.name, phone: c.phone, email: c.email };
    for (const [slot, key] of Object.entries(keys)) {
      const v = vals[slot] ?? "";
      if (v) onField(lead.address, key, v);
    }
  };
  return (
    <div className="pdcard">
      <div className="pdsec">{title}</div>
      {pickKind && opts.length ? <PastContactPicker options={opts} kind={pickKind} onPick={fillFrom} /> : null}
      <div className="kvgrid"><FieldGrid lead={lead} fields={fields} group={group} onField={onField} /></div>
    </div>
  );
}

/** v1 PROPERTY INFORMATION card — read-only header rows + the property group. */
function PropertyInfoCard({ lead, fields, onField }: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onField: (address: string, key: string, value: string | null) => void;
}) {
  const isPhone = usePhone();
  const county = lead.county ? lead.county.replace(/\s*county\s*$/i, "") + " County" : "—";
  const ro: [string, string][] = [
    ["County", county],
    ["Bedrooms", lead.beds != null ? String(lead.beds) : "—"],
    ["Bathrooms", lead.baths != null ? String(lead.baths) : "—"],
    ["Living Sqft", lead.sqft != null ? lead.sqft.toLocaleString() : "—"],
  ];
  return (
    <div className="pdcard">
      <div className="pdsec">PROPERTY INFORMATION</div>
      <div className="kvgrid">
        {isPhone ? (
          /* four label-over-value cells become two slim rows — beds, baths and
             sqft read as one figure anyway */
          <>
            <div className="kvread ro"><span className="kvread-l">County</span><span className="kvread-v">{county}</span></div>
            <div className="kvread ro"><span className="kvread-l">Beds / baths · sqft</span><span className="kvread-v">{lead.beds ?? "—"} / {lead.baths ?? "—"} · {lead.sqft ? lead.sqft.toLocaleString() : "—"}</span></div>
          </>
        ) : ro.map(([l, v]) => (
          <div key={l} className="kvcell"><div className="fld-l">{l}</div><div className="fld-v">{v}</div></div>
        ))}
        <FieldGrid lead={lead} fields={fields} group="property" onField={onField} />
        {(() => {
          const clink = (typeof fields?.["countyLink"] === "string" && fields["countyLink"]) ? (fields["countyLink"] as string) : (lead.countyLink ?? "");
          return clink ? (
            <div className="kvcell"><div className="fld-l">County Records</div>
              <a className="proplinkurl" href={clink} target="_blank" rel="noreferrer">View county appraiser record</a></div>
          ) : null;
        })()}
        {lead.countySite ? (
          <div className="kvcell"><div className="fld-l">Appraiser Site</div>
            <a className="proplinkurl" href={lead.countySite} target="_blank" rel="noreferrer">{lead.countySite.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a></div>
        ) : null}
        <div className="kvcell"><div className="fld-l">Google Street View</div>
          <a className="proplinkurl" href={`https://www.google.com/maps?q&layer=c&cbll=${encodeURIComponent(lead.address)}`} target="_blank" rel="noreferrer">View Street View</a></div>
      </div>
    </div>
  );
}

/** v1 FINANCIALS card — Asking + Listed/Comped ARV + Zest/Rent + financials. */
function FinancialsCard({ lead, fields, onField, onArv }: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onField: (address: string, key: string, value: string | null) => void;
  onArv: (address: string, kind: "listedArv" | "compedArv", value: number | null) => void;
}) {
  const isPhone = usePhone();
  const [editingArv, setEditingArv] = useState<Set<string>>(() => new Set());
  useEffect(() => { setEditingArv(new Set()); }, [lead.id]);
  const initOf = (key: string): string | null => {
    const v = fields?.[key];
    return typeof v === "string" ? v : null;
  };
  /** Phone: the two ARV inputs are tap-to-edit rows like every other field —
   *  but always visible even when empty, because ARV drives ROI and hiding
   *  the most important blank on the page behind an expander helps nobody. */
  const arvRow = (key: "listedArv" | "compedArv", label: string, placeholder: string) =>
    !isPhone || editingArv.has(key) || initOf(key) ? (
      isPhone && !editingArv.has(key) && initOf(key) ? (
        <button type="button" className="kvread" title="Tap to edit" onClick={() => setEditingArv((s) => new Set(s).add(key))}>
          <span className="kvread-l">{label}</span><span className="kvread-v">{initOf(key)}</span>
        </button>
      ) : (
        <div className="kvcell" ref={isPhone ? focusOnMount : undefined}>
          <div className="fld-l">{label}</div>
          <MoneyText key={lead.id + ":" + key} initial={initOf(key)} placeholder={placeholder} onCommit={(v) => onArv(lead.address, key, parseMoneyEdit(v))} />
        </div>
      )
    ) : (
      <button type="button" className="kvread" onClick={() => setEditingArv((s) => new Set(s).add(key))}>
        <span className="kvread-l">{label}</span><span className="kvread-v dim">—</span>
      </button>
    );
  return (
    <div className="pdcard">
      <div className="pdsec">FINANCIALS</div>
      <div className="kvgrid">
        {isPhone ? (
          <>
            <div className="kvread ro"><span className="kvread-l">Asking price</span><span className="kvread-v">{fmtMoney(lead.price)}</span></div>
            {arvRow("listedArv", "Listed ARV", "e.g. 300,000")}
            {arvRow("compedArv", "Comped ARV", "wins over listed")}
            {/* Zestimate rows only when Zillow gave us one — a read-only "—"
                answers no question and cannot be filled */}
            {lead.zest != null ? <div className="kvread ro"><span className="kvread-l">Zestimate</span><span className="kvread-v">{fmtMoneyEdit(lead.zest)}</span></div> : null}
            {lead.rentZest != null ? <div className="kvread ro"><span className="kvread-l">Rent Zestimate</span><span className="kvread-v">{fmtMoneyEdit(lead.rentZest)}</span></div> : null}
          </>
        ) : (
          <>
        <div className="kvcell"><div className="fld-l">Asking Price</div><div className="fld-v">{fmtMoney(lead.price)}</div></div>
        <div className="kvcell">
          <div className="fld-l">Listed ARV</div>
          <MoneyText key={lead.id + ":flisted"} initial={initOf("listedArv")} placeholder="e.g. 300,000" onCommit={(v) => onArv(lead.address, "listedArv", parseMoneyEdit(v))} />
        </div>
        <div className="kvcell">
          <div className="fld-l">Comped ARV</div>
          <MoneyText key={lead.id + ":fcomped"} initial={initOf("compedArv")} placeholder="wins over listed" onCommit={(v) => onArv(lead.address, "compedArv", parseMoneyEdit(v))} />
        </div>
        <div className="kvcell"><div className="fld-l">Zestimate</div><div className="fld-v">{lead.zest != null ? fmtMoneyEdit(lead.zest) : "—"}</div></div>
        <div className="kvcell"><div className="fld-l">Rent Zestimate</div><div className="fld-v">{lead.rentZest != null ? fmtMoneyEdit(lead.rentZest) : "—"}</div></div>
          </>
        )}
        <FieldGrid lead={lead} fields={fields} group="financials" onField={onField} />
      </div>
    </div>
  );
}

/** v1 AUTO COMPS card — read-only scraped metrics + a link to the comps tool. */
function AutoCompsCard({ lead, onOpenComps }: { lead: Lead; onOpenComps: () => void }) {
  const ro: [string, string][] = [
    ["Auto ARV", lead.arv != null ? fmtMoneyEdit(lead.arv) : "—"],
    ["Asking / ARV", lead.arvPct != null ? lead.arvPct + "%" : "—"],
    ["Avg CDOM", lead.cdom != null ? String(lead.cdom) : "—"],
    ["Year Built", lead.yearBuilt != null ? String(lead.yearBuilt) : "—"],
  ];
  return (
    <div className="pdcard">
      <div className="pdcardhd"><div className="pdsec pdsecflush">AUTO COMPS</div><button className="dlink" onClick={onOpenComps}>Open comps tool →</button></div>
      <div className="kvgrid">
        {ro.map(([l, v]) => (
          <div key={l} className="kvcell"><div className="fld-l">{l}</div><div className="fld-v">{v}</div></div>
        ))}
      </div>
    </div>
  );
}

/** v1 LINKS card — Zillow / Listing deep links + user photo/video links. */
function LinksCard({ address, fields, onField, links, onSaveMediaLinks, scraped = [], auto = {} }: {
  address: string;
  fields: Record<string, unknown> | undefined;
  /** links the enrichment found (raw.property_url → Zillow) — shown when the
   *  user hasn't typed their own; editing still writes the user's field */
  auto?: Partial<Record<string, string | null>>;
  onField: (address: string, key: string, value: string | null) => void;
  links: MediaLink[];
  onSaveMediaLinks: (address: string, links: MediaLink[]) => void;
  /** scraped photo/video links from the lead (read-only, no remove). */
  scraped?: { label: string; url: string }[];
}) {
  const rows: [string, string][] = [["zillowUrl", "Zillow Link"], ["listingUrl", "Listing Link"]];
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [mlabel, setMlabel] = useState("");
  const [murl, setMurl] = useState("");
  const [adding, setAdding] = useState(false);
  const val = (kk: string) => (typeof fields?.[kk] === "string" && (fields[kk] as string).trim() ? (fields[kk] as string) : (auto[kk] ?? ""));
  const addMedia = () => {
    const u = murl.trim();
    if (!u) return;
    onSaveMediaLinks(address, [...links, { label: mlabel.trim() || "Photos", url: u }]);
    setMlabel(""); setMurl(""); setAdding(false);
  };
  return (
    <div className="pdcard">
      <div className="pdsec">LINKS</div>
      <div className="linkslist">
        {rows.map(([key, label]) => {
          const cur = val(key);
          return (
            <div key={key} className="linkrow">
              <div className="fld-l linkhd"><span>{label}</span>{editing !== key ? <button className="rowbtn" title="Edit" onClick={() => { setDraft(cur); setEditing(key); }}>✎</button> : null}</div>
              {editing === key ? (
                <div className="linkedit">
                  <input className="proplinkinput" autoFocus placeholder="https://…" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onField(address, key, draft.trim() || null); setEditing(null); } if (e.key === "Escape") setEditing(null); }} />
                  <button className="btn slim" onClick={() => { onField(address, key, draft.trim() || null); setEditing(null); }}>Save</button>
                </div>
              ) : cur ? (
                <a href={cur} target="_blank" rel="noreferrer" className="proplinkurl">{cur}</a>
              ) : (
                <button className="linkbtn" onClick={() => { setDraft(""); setEditing(key); }}>+ Add link</button>
              )}
            </div>
          );
        })}
        {scraped.map((m, i) => (
          <div key={"s" + i} className="linkrow">
            <div className="fld-l linkhd"><span>{m.label} Link</span></div>
            <a href={m.url} target="_blank" rel="noreferrer" className="proplinkurl">{m.url}</a>
          </div>
        ))}
        {links.map((m, i) => (
          typeof m.url === "string" && m.url.startsWith("data:") ? null : (
            <div key={"m" + i} className="linkrow">
              <div className="fld-l linkhd"><span>{m.label} Link</span><button className="rowbtn del" title="Remove" onClick={() => onSaveMediaLinks(address, links.filter((_, j) => j !== i))}>✕</button></div>
              <a href={m.url} target="_blank" rel="noreferrer" className="proplinkurl">{m.url}</a>
            </div>
          )
        ))}
        {adding ? (
          <div className="linkedit medadd">
            <input className="proplinkinput medlabel" placeholder="Label" value={mlabel} onChange={(e) => setMlabel(e.target.value)} />
            <input className="proplinkinput" placeholder="https://…" value={murl} onChange={(e) => setMurl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMedia()} />
            <button className="btn slim" onClick={addMedia}>Add</button>
          </div>
        ) : (
          <button className="linkbtn" onClick={() => setAdding(true)}>+ Add photo / video link</button>
        )}
      </div>
    </div>
  );
}

/** v1 NOTES card. */
function NotesCard({ value, onChange, onCommit }: { value: string; onChange: (v: string) => void; onCommit: (v: string) => void }) {
  return (
    <div className="pdcard">
      <div className="pdsec">NOTES</div>
      <textarea className="notes" rows={6} placeholder="Add notes about this property…" value={value} onChange={(e) => onChange(e.target.value)} onBlur={(e) => onCommit(e.target.value)} />
    </div>
  );
}

/** v1 ACTIVITY LOG card. */
function ActivityLogCard({ lead, fields }: { lead: Lead; fields: Record<string, unknown> | undefined }) {
  const rows = leadActivity({ scraped: !lead.id.startsWith("m:"), source: lead.source, photos: lead.photos.length, owner: typeof fields?.["owner"] === "string" ? (fields["owner"] as string) : "" });
  return (
    <div className="pdcard">
      <div className="pdsec">ACTIVITY LOG</div>
      {rows.length === 0 ? <div className="sub">No activity yet.</div> : (
        <div className="leadactivity">
          {rows.map((r, i) => <div key={i} className="leadactrow"><span className="leadactdot" />{r}</div>)}
        </div>
      )}
    </div>
  );
}

/** v1 photo thumbnail card — first 3 photos, click to open the Photos tab. */
function PhotoThumbCard({ lead, extra = [], onViewAll }: { lead: Lead; extra?: string[]; onViewAll: () => void }) {
  const ph = [...lead.photos.filter(Boolean), ...extra];
  if (ph.length === 0) {
    return (
      <div className="pdcard photocard">
        <div className="photothumbgrid">{[0, 1, 2].map((i) => <div key={i} className="ptcell empty">🏠</div>)}</div>
        <div className="ptnote">No photos scraped</div>
      </div>
    );
  }
  return (
    <button className="pdcard photocard photobtn" onClick={onViewAll}>
      <div className="photothumbgrid">{ph.slice(0, 3).map((u, i) => <div key={u + i} className="ptcell"><img src={u} alt="" loading="lazy" /></div>)}</div>
      <div className="ptnote link">{ph.length} photo{ph.length > 1 ? "s" : ""} — view all</div>
    </button>
  );
}

/** v1 ROI CALCULATOR (buyers-agent.js calcROI) — the simple, always-visible
 * Comped ARV / Repairs / Offer Price → Net ARV ×0.94 → Expected Profit block.
 * Comped ARV and Offer Price commit to the same LEAD_EDITS keys on blur; the
 * full Flip/BRRRR calculator still lives in the Net ROI tab. */
function QuickRoiCard({ lead, fields, onArv, onField, rehabEst }: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onArv: (address: string, kind: "listedArv" | "compedArv", value: number | null) => void;
  onField: (address: string, key: string, value: string | null) => void;
  rehabEst: number;
}) {
  const seedArv =
    parseMoneyEdit(typeof fields?.["compedArv"] === "string" ? (fields["compedArv"] as string) : null) ??
    parseMoneyEdit(typeof fields?.["listedArv"] === "string" ? (fields["listedArv"] as string) : null) ??
    lead.arv ?? 0;
  const seedOffer =
    parseMoneyEdit(typeof fields?.["offerPrice"] === "string" ? (fields["offerPrice"] as string) : null) ??
    lead.price ?? 0;
  const [arv, setArv] = useState(seedArv);
  const [rep, setRep] = useState(rehabEst);
  const [off, setOff] = useState(seedOffer);
  useEffect(() => {
    setArv(seedArv);
    setRep(rehabEst);
    setOff(seedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);
  const net = arv * 0.94;
  const profit = net - rep - off;
  const pct = off > 0 ? (profit / off) * 100 : 0;
  const tier = pct >= 20 ? "good" : pct > 0 ? "ok" : "bad";
  return (
    <div className="pdcard">
      <div className="pdcardhd"><div className="pdsec pdsecflush">ROI CALCULATOR</div><span className={"roibadge r-" + tier}>{pct.toFixed(1)}%</span></div>
      <div className="quickroigrid">
        <div className="arvfield"><label className="lbl">Comped ARV</label><input className="moneyinput" inputMode="decimal" value={arv || ""} placeholder="0" onChange={(e) => setArv(toNum(e.target.value))} onBlur={() => onArv(lead.address, "compedArv", arv || null)} /></div>
        <div className="arvfield"><label className="lbl">Repairs</label><input className="moneyinput" inputMode="decimal" value={rep || ""} placeholder="0" onChange={(e) => setRep(toNum(e.target.value))} /></div>
        <div className="arvfield"><label className="lbl">Offer Price</label><input className="moneyinput" inputMode="decimal" value={off || ""} placeholder="0" onChange={(e) => setOff(toNum(e.target.value))} onBlur={() => onField(lead.address, "offerPrice", off ? fmtMoneyEdit(off) : null)} /></div>
      </div>
      <div className="quickroibrk">
        <div className="qrow"><span className="sub">Comped ARV</span><strong>{usd(arv)}</strong></div>
        <div className="qrow"><span className="sub">Net ARV (× 0.94, ~6% sell costs)</span><strong>{usd(net)}</strong></div>
        <div className="qrow"><span className="sub">Repairs</span><strong className="qneg">- {usd(rep)}</strong></div>
        <div className="qrow"><span className="sub">Offer Price</span><strong className="qneg">- {usd(off)}</strong></div>
        <div className="qrow qtot"><span>Expected Profit</span><strong className={"r-" + tier}>{usd(profit)}</strong></div>
        <div className="qrow qtot"><span>Profit %</span><strong className={"r-" + tier}>{pct.toFixed(1)}%</strong></div>
      </div>
    </div>
  );
}
/** v1 detail-arv.js listingPanel — the Listing tab's grouped read/edit cards:
 * Property Details, Price Information, Financial, Tax & Legal. Deal Engine,
 * Listing Overview and Price History are rendered around it by the caller. */
function ListingSections({ lead, fields, onField, hiddenFields, rehabVal, roi, countyLabel }: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onField: (address: string, key: string, value: string | null) => void;
  hiddenFields: Set<string>;
  rehabVal: number;
  roi: import("../roi/estMoney").LeadRoi;
  countyLabel: string;
}) {
  const val = (key: string): string | null => {
    const v = fields?.[key];
    return typeof v === "string" && v.trim() ? v : null;
  };
  const commit = (key: string, v: string | null) =>
    onField(lead.address, key, v !== null && /phone/i.test(key) ? normPhone(v) || v : v);
  const ro = (label: string, value: string) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div><div className="fld-v">{value}</div></div>
  );
  const txt = (label: string, key: string) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div>
      <TextField key={lead.id + key} initial={val(key)} onCommit={(v) => commit(key, v)} /></div>
  );
  const money = (label: string, key: string) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div>
      <MoneyText key={lead.id + key} initial={val(key)} placeholder="—" onCommit={(v) => commit(key, v)} /></div>
  );
  const sel = (label: string, key: string, opts: string[]) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div>
      <select className="moneyinput" value={val(key) ?? ""} onChange={(e) => commit(key, e.target.value || null)}>
        <option value="">—</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select></div>
  );
  return (
    <>
      <div className="pdcard">
        <div className="pdsec">PROPERTY DETAILS</div>
        <div className="kvgrid">
          {sel("Property Type", "ptype", OPT_PTYPE)}
          {sel("Property Label", "label", OPT_LABEL)}
          {txt("Year Built", "yearBuilt")}
          {ro("Square Feet", lead.sqft != null ? lead.sqft.toLocaleString() : "—")}
          {ro("Beds / Baths", `${lead.beds ?? "—"} / ${lead.baths ?? "—"}`)}
          {txt("Lot Size", "lotSize")}
          {ro("County", countyLabel)}
        </div>
      </div>
      <div className="pdcard">
        <div className="pdsec">PRICE INFORMATION</div>
        <div className="kvgrid">
          {ro("Asking Price", fmtMoney(lead.price))}
          {ro("ARV", lead.arv != null ? fmtMoneyEdit(lead.arv) : "—")}
          {ro("Zestimate", lead.zest != null ? fmtMoneyEdit(lead.zest) : "—")}
          {ro("Rent Zestimate", lead.rentZest != null ? fmtMoneyEdit(lead.rentZest) : "—")}
        </div>
      </div>
      {hiddenFields.has("financials") ? null : (
        <div className="pdcard">
          <div className="pdsec">FINANCIAL</div>
          <div className="kvgrid">
            {ro("Rehab Est.", rehabVal > 0 ? usd(rehabVal) : "—")}
            {ro("Est. ROI", roi.roi !== null ? roi.roi.toFixed(1) + "%" : "—")}
            {money("Offer Price", "offerPrice")}
            {sel("Offer Made", "offerMade", OPT_YESNO)}
            {sel("Offer Accepted", "offerAccepted", OPT_YESNO)}
          </div>
        </div>
      )}
      {hiddenFields.has("taxLegal") ? null : (
        <div className="pdcard">
          <div className="pdsec">TAX &amp; LEGAL</div>
          <div className="kvgrid">
            {ro("Parcel ID", val("parcel") ?? "—")}
            {ro("Annual Taxes", val("countyTax") ?? "—")}
            {ro("Assessed Value", val("assessed") ?? "—")}
            {ro("Subdivision", val("subdivision") ?? "—")}
            {ro("Land Description", val("legalDesc") ?? "—")}
          </div>
        </div>
      )}
    </>
  );
}

/** v1 detail-arv.js taxPanel — the Tax Info tab's editable grids: Flood &
 * Zoning, Valuation & Tax, County Characteristics. Public tax history + the
 * disclaimer are rendered around it by the caller. */
export function TaxSections({ lead, fields, onField }: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  onField: (address: string, key: string, value: string | null) => void;
}) {
  const val = (key: string): string | null => {
    const v = fields?.[key];
    return typeof v === "string" && v.trim() ? v : null;
  };
  const commit = (key: string, v: string | null) =>
    onField(lead.address, key, v !== null && /phone/i.test(key) ? normPhone(v) || v : v);
  const ro = (label: string, value: string) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div><div className="fld-v">{value}</div></div>
  );
  const txt = (label: string, key: string) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div>
      <TextField key={lead.id + key} initial={val(key)} onCommit={(v) => commit(key, v)} /></div>
  );
  const money = (label: string, key: string) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div>
      <MoneyText key={lead.id + key} initial={val(key)} placeholder="—" onCommit={(v) => commit(key, v)} /></div>
  );
  const sel = (label: string, key: string, opts: string[]) => (
    <div key={label} className="kvcell"><div className="fld-l">{label}</div>
      <select className="moneyinput" value={val(key) ?? ""} onChange={(e) => commit(key, e.target.value || null)}>
        <option value="">—</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select></div>
  );
  return (
    <>
      <div className="pdcard">
        <div className="pdsec">FLOOD &amp; ZONING</div>
        <div className="kvgrid">
          {sel("Flood Zone", "flood", OPT_FLOOD)}
          {sel("Water Source", "water", OPT_WATER)}
          {sel("Sewer / Septic", "septic", OPT_SEWER)}
        </div>
      </div>
      <div className="pdcard">
        <div className="pdsec">VALUATION &amp; TAX</div>
        <div className="kvgrid">
          {money("Assessed Value", "assessed")}
          {money("County Annual Tax", "countyTax")}
          {money("Annual Tax (Zillow)", "taxAmount")}
          {ro("Zestimate", lead.zest != null ? fmtMoneyEdit(lead.zest) : "—")}
          {ro("Rent Zestimate", lead.rentZest != null ? fmtMoneyEdit(lead.rentZest) : "—")}
          {txt("Avg $ / Sqft", "pricePerSqft")}
        </div>
      </div>
      <div className="pdcard">
        <div className="pdsec">COUNTY CHARACTERISTICS</div>
        <div className="kvgrid">
          {txt("County Owner", "owner")}
          {txt("Living Sqft (County)", "countySqft")}
          {txt("Total Building Sqft", "totalSqft")}
          {txt("Lot Acres", "lotAcres")}
          {sel("Architectural Style", "archStyle", OPT_ARCH)}
          {sel("Roof Type", "roofType", OPT_ROOF)}
          {sel("Construction", "construction", OPT_CONSTR)}
          {sel("Foundation Type", "foundation", OPT_FOUND_TYPE)}
          {sel("Parking", "parking", OPT_PARKING)}
        </div>
      </div>
    </>
  );
}

/** Messages & Calls card (v1 detail comms panel) — the lead's SMS thread from
 * the shared comms data, with a LIVE composer (go-live item 5, quoSend) and
 * click-to-call (item 6, quoCall). Thread matching: any message stamped with
 * this lead's key, else the seller's phone number as the peer. Honest states:
 * the composer locks while sending and the message appears once the server
 * confirms (the caller refreshes threads). */
function LeadCommsCard({ leadKey, seller, threads, onEmail, onSendSms, onCallLead, onSaveSeller, dispName, dispPhone, onSavePhone }: {
  leadKey: string;
  seller: SellerContact | null;
  threads: import("../inbox/api").Thread[];
  onEmail: () => void;
  onSendSms?: (to: string, text: string, leadKey: string, name?: string, idemKey?: string) => Promise<void>;
  onCallLead?: (to: string, leadKey: string, name?: string) => Promise<void>;
  /** save the recovered sender as the seller contact (one click, from here) */
  onSaveSeller?: (s: SellerContact) => void;
  /** dispo contact (Tom 2026-08-11: "option for either the seller phone
   * number or the dispo phone number") */
  dispName?: string;
  dispPhone?: string;
  /** save a manually-typed number straight from this panel */
  onSavePhone?: (target: "seller" | "dispo", phone: string) => void;
}) {
  const canSend = liveOn("quoSend") && !!onSendSms;
  const canCall = liveOn("quoCall") && !!onCallLead;
  const sellerPh = seller?.phone ? normPhone(seller.phone) || seller.phone : "";
  const dispPh = dispPhone ? normPhone(dispPhone) || dispPhone : "";
  const matched = useMemo(() => {
    const mine = threads.filter((t) =>
      t.messages.some((m) => m.leadKey === leadKey) || (sellerPh && t.peer === sellerPh));
    return mine.sort((a, b) => b.lastAt - a.lastAt)[0] ?? null;
  }, [threads, leadKey, sellerPh]);

  /** WHOEVER SENT THE TEXT IS THE CONTACT (Tom, 2026-07-28).
   *
   * The backend applies that rule when it CREATES a lead from a text, but only
   * from 2026-07-27 on — and only at creation. Every SMS lead taken before
   * that, and any whose parse missed the sender, is stranded: the whole lead
   * exists because of a conversation we already have, and the panel still says
   * "no messages, add a phone" with no way to know which phone.
   *
   * So when there's no contact on file, find the inbound text that this lead
   * came from — its body contains the street address, which is why the lead
   * exists — and treat its sender as the contact. Retroactive, no migration,
   * and it stays correct for anything the parser misses in future. Read-only
   * until the user accepts it; we suggest, they save. */
  const recovered = useMemo(
    () => (matched ? null : recoverContactThread(leadKey, threads)),
    [matched, threads, leadKey],
  );

  const thread = matched ?? recovered;
  // RECIPIENT (Tom 2026-08-11): seller OR dispo — his pick when both exist.
  // An open thread keeps its own peer (that's the conversation on screen).
  const [target, setTarget] = useState<"seller" | "dispo">(sellerPh ? "seller" : "dispo");
  const chosenPh = target === "dispo" ? (dispPh || sellerPh) : (sellerPh || dispPh);
  const to = thread?.peer || chosenPh;
  const usingDispo = !thread && to !== "" && to === dispPh && to !== sellerPh;
  // NOT thread.name unfiltered: contact_name gets stamped with the property
  // address when we text about a lead, so the composer read "Text 4732 W Bay
  // Vista Ave, Tampa, FL 33611" and sending would stamp that address onto the
  // new row too. Blank is better than wrong — the server falls back to the number.
  const who = (usingDispo && isPersonName(dispName) && dispName)
    || (isPersonName(seller?.name) && seller?.name)
    || (isPersonName(thread?.name) && thread?.name)
    || "";
  // manual phone entry, right here (Tom: "option to insert phone number
  // manually") — saved to the Seller card or the Dispo field, his choice
  const [phoneDraft, setPhoneDraft] = useState("");
  const savePhone = () => {
    const digits = phoneDraft.replace(/[^0-9+]/g, "");
    if (digits.replace(/[^0-9]/g, "").length < 10 || !onSavePhone) return;
    onSavePhone(target, normPhone(digits) || digits);
    setPhoneDraft("");
  };
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [callNote, setCallNote] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  // RECOVERED threads are matched by phone NUMBER, and wholesalers blast many
  // deals from one number — so the tail of the thread can be about a totally
  // different house (Tom 2026-08-11: "why do i see messages that has nothing
  // to do with this lead"). For a recovered thread, show ONLY the messages
  // that mention THIS property's street line. A directly-matched thread
  // (lead_key linked) keeps the full conversation as before.
  const streetLine = (leadKey.split(",")[0] ?? "").trim().toLowerCase();
  const msgs = thread
    ? (recovered && !matched && streetLine.length >= 8
        ? thread.messages.filter((m) => m.body.toLowerCase().includes(streetLine))
        : thread.messages
      ).slice(-10)
    : [];
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [thread?.count]);
  const idemRef = useRef<string | null>(null); // per-draft send key (idempotency)
  const send = async () => {
    const text = draft.trim();
    if (!text || !to || sending || !onSendSms) return;
    setSending(true);
    setNote(null);
    if (!idemRef.current) idemRef.current = newIdemKey();
    try {
      await onSendSms(to, text, leadKey, who || undefined, idemRef.current);
      idemRef.current = null; // confirmed — next draft gets a fresh key
      setDraft("");
    } catch (e) {
      setNote(((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Send failed").slice(0, 120));
    } finally {
      setSending(false);
    }
  };
  const call = async () => {
    if (!to || !onCallLead) return;
    setCallNote("Calling…");
    try {
      await onCallLead(to, leadKey, who || undefined);
      setCallNote("Ringing your phone first ✓");
      setTimeout(() => setCallNote(null), 4000);
    } catch (e) {
      setCallNote(((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Call failed").slice(0, 80));
    }
  };
  return (
    <div className="pdcomms">
      <div className="cmhd">
        <div className="cmttl">💬 Messages &amp; Calls</div>
        <div className="cmbtns">
          <button className="cmbtn email" onClick={onEmail}>✉ Email</button>
          {canCall ? (
            <button className="cmbtn call" disabled={!to} title={to ? "Call " + (who || to) : "No phone on file — add the seller contact"} onClick={() => void call()}>📞 Call</button>
          ) : (
            <button className="cmbtn call" disabled title="Calling goes live in its own step">📞 Call</button>
          )}
        </div>
      </div>
      {callNote ? <div className="sub cmcallnote">{callNote}</div> : null}
      {recovered ? (
        <div className="cmrecover">
          <span>
            This lead came from a text from <strong>{contactLabel(recovered.name, recovered.peer)}</strong>.
            No contact is saved on the lead, so nothing else here is linked to them.
            Only their texts about THIS property are shown — the full conversation is in the Inbox.
          </span>
          {onSaveSeller ? (
            <button
              className="btn slim"
              onClick={() => onSaveSeller({
                // never seed the contact card with an address-shaped "name"
                name: isPersonName(recovered.name) ? (recovered.name as string).trim() : "",
                role: "Wholesaler", // the usual sender of a deal text; editable in the Seller card
                phone: recovered.peer,
                email: "",
                company: "",
              })}
            >Save as the contact</button>
          ) : null}
        </div>
      ) : null}
      <div className="cmbody">
        {msgs.length === 0 ? (
          <div className="cmempty">
            {to
              ? "No messages with " + (who || to) + " yet — say hello below."
              : "No messages synced for this lead yet.\nAdd the seller's phone (Seller Contact card) to text them."}
          </div>
        ) : (
          <>
            {msgs.map((m) => (
              <div key={m.id} className={"cmmsg " + (m.direction === "in" ? "in" : "out")}>
                {m.body || (m.channelIsCall ? "📞 Call" : "—")}
                <div className="cmtime">{new Date(m.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {new Date(m.createdAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</div>
              </div>
            ))}
            <div ref={endRef} />
          </>
        )}
      </div>
      {note ? <div className="cmsenderr">{note}</div> : null}
      <div className="cmfoot">
        {!thread && sellerPh && dispPh && sellerPh !== dispPh ? (
          <div className="cmto">
            <span className="cmtol">To:</span>
            <button className={"cmtobtn" + (target === "seller" ? " on" : "")} onClick={() => setTarget("seller")}>
              Seller {fmtPhone(sellerPh)}
            </button>
            <button className={"cmtobtn" + (target === "dispo" ? " on" : "")} onClick={() => setTarget("dispo")}>
              Dispo {fmtPhone(dispPh)}
            </button>
          </div>
        ) : null}
        {!to && onSavePhone ? (
          <div className="cmaddphone">
            <input
              placeholder="(407) 555-0123"
              inputMode="tel"
              value={phoneDraft}
              onChange={(e) => setPhoneDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") savePhone(); }}
            />
            <select value={target} onChange={(e) => setTarget(e.target.value as "seller" | "dispo")}>
              <option value="seller">Seller #</option>
              <option value="dispo">Dispo #</option>
            </select>
            <button className="btn slim" disabled={phoneDraft.replace(/[^0-9]/g, "").length < 10} onClick={savePhone}>Save</button>
          </div>
        ) : null}
        {canSend ? (
          <div className="cmcompose">
            <input
              placeholder={to ? "Text " + (who || fmtPhone(to)) + (usingDispo ? " (dispo)" : "") + "…" : "Add a phone number above first"}
              value={draft}
              disabled={!to || sending}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void send(); }}
            />
            <button className="cmsend" disabled={!to || sending || !draft.trim()} title="Send SMS" onClick={() => void send()}>{sending ? "…" : "➤"}</button>
          </div>
        ) : (
          <div className="cmcompose"><input placeholder="Sending is off on this beta" disabled /><button className="cmsend" disabled>➤</button></div>
        )}
      </div>
    </div>
  );
}

/** v1 _photoLinksHTML — "Photos & Docs from Contact": Dropbox/Drive/gallery
 * links the seller or agent sent over. Hidden when there are none. */
export function ContactDocsCard({ docs }: { docs: { label: string; url: string }[] }) {
  if (!docs.length) return null;
  return (
    <div className="pdcard">
      <div className="pdsec">PHOTOS &amp; DOCS FROM CONTACT</div>
      <div className="photolinks">
        {docs.map((m, i) => {
          const vid = /video|youtu|vimeo|tour|walk|\.mp4/i.test((m.label || "") + " " + m.url);
          return <a key={i} href={m.url} target="_blank" rel="noreferrer" className={"photolink" + (vid ? " vid" : "")}>{vid ? "▶ " : "🖼 "}{m.label || "Link"}</a>;
        })}
      </div>
    </div>
  );
}
